import { FC, useState, useMemo, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Paper,
  Typography,
  Button,
  ButtonGroup,
  Checkbox,
  ClickAwayListener,
  Grow,
  ListItemText,
  MenuList,
  Popper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  IconButton,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Stack,
  Tabs,
  Tab,
  Chip,
  Card,
  CardContent,
  InputAdornment,
  Grid,
  FormControl,
  InputLabel,
  Select,
  Autocomplete,
  FormHelperText,
  Divider,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Download as DownloadIcon,
  Add as AddIcon,
  TrendingUp as TrendingUpIcon,
  Schedule as ScheduleIcon,
  Warning as WarningIcon,
  AttachMoney as MoneyIcon,
  Visibility as VisibilityIcon,
  Receipt as ReceiptIcon,
  ArrowBack as ArrowBackIcon,
  CallSplit as CallSplitIcon,
  Payment as PaymentIcon,
  Link as LinkIcon,
  ArrowDropDown as ArrowDropDownIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import api, { Client, clientApi, billingApi, batchPaymentApi, invoiceSettingsApi, InvoiceSettings } from '../../utils/api';
import {
  recordablePaymentTypeOptions,
  defaultRecordablePaymentType,
  paymentTypesLoadState,
  PaymentTypeOption,
} from '../../utils/paymentTypes';
import { format, addDays, subDays, startOfDay, startOfYear, parseISO, isValid } from 'date-fns';
import InvoiceDetail from '../../components/InvoiceDetail';
import { billableOwnersOf } from '../../utils/invoiceMath';
import InvoiceLineItemsEditor, { LineItem, CatalogOption, InvoiceDiscount, applyLineDiscount, calculateTax, computeInvoiceDiscountAmount } from '../../components/InvoiceLineItemsEditor';
import { APPOINTMENT_PRICING } from '../../constants/appointmentPricing';
import EnhancedPaymentDialog from '../../components/EnhancedPaymentDialog';
import SendPaymentLinkDialog from '../../components/SendPaymentLinkDialog';
import ClientInvoiceWizard from '../../components/ClientInvoiceWizard';
import StatementsTab from './StatementsTab';
import { serviceCatalogService, ServiceCatalogItem } from '../../services/serviceCatalogService';
import { useSnackbar } from 'notistack';
import { useAnalytics } from '../../hooks/useAnalytics';
import {
  withOfflineFallback,
  skipWhenOffline,
  useIsOffline,
  executeWrite,
  executeUpdate,
  OfflineWriteBlockedError,
} from '../../offline';
import { readInvoicesPage, readInvoice, readInvoiceStats, readHorsesPage, OPEN_INVOICE_STATUSES } from '../../offline/readers';
import { formatInstantInZone, todayLocalISO, ymdToNoonUtcIso } from '../../utils/dateUtils';
import { useClinicRecord, useClinicTimezone } from '../../hooks/useClinicTimezone';
import { formatDollars } from '../../utils/money';
import { patientLabel } from '../../utils/patientLabel';
import { useRBAC } from '../../auth/useRBAC';
import { invoicingControls } from '../../auth/pageControls';
import { isCancelled } from '../Appointments/appointmentStatus';

interface Owner {
  name: string;
  email?: string;
  phone?: string;
  ownershipPercentage?: number | null;
}

interface Horse {
  id: string;
  name: string;
  patientId?: string;
  owners: Owner[];
}

// The invoice form's patient field matches typed text against this whole label,
// so a horse is found by its name, its patient ID or its first owner.
const invoicePatientLabel = (horse: Horse): string =>
  `${patientLabel(horse)} (Owner: ${(Array.isArray(horse.owners) && horse.owners[0]?.name) || 'Unknown'})`;

interface Appointment {
  id: string;
  date: string;
  type: string;
  location?: string;
  horseId: string;
  horse: Horse;
  status?: string;
  invoiceId?: string;
  invoice?: {
    id: string;
    invoiceNumber: string;
  };
}

interface Invoice {
  id: string;
  invoiceNumber: string;
  appointments?: Array<{
    id: string;
    type: string;
    date?: string;
    location?: string;
  }>;
  horseId: string;
  clientId?: string;
  splitGroupId?: string;
  horse: {
    name: string;
    /** The patient's ownership list. NOT the bill-to — the four `owner*` fields below are. */
    owners: Owner[];
    // All four are the SERVER-resolved BILL-TO (billing/bill-to.ts): on a third-party
    // invoice they carry the payer's details, and a payer snapshot with no email drops
    // `ownerEmail` from the response entirely. Declared because this page forwards the
    // whole `horse` object into SendPaymentLinkDialog, which reads `ownerName`/`ownerEmail`
    // — undeclared, the compiler could not see a hand-built object drop one (M-2).
    ownerName?: string;
    ownerEmail?: string;
    ownerPhone?: string;
    ownerAddress?: string;
  };
  client?: {
    id: string;
    name: string;
    email?: string;
    phone?: string;
  };
  // One-time payer snapshot stored on the invoice (no client record)
  thirdPartyPayer?: {
    name: string;
    email?: string;
    phone?: string;
    address?: string;
  };
  amount: number;
  status: 'pending' | 'paid' | 'partially_paid' | 'overdue' | 'cancelled';
  description?: string;
  // The client note (ADR-0044); an older web invoice keeps its note in `description`.
  clientNote?: string | null;
  issuedAt: string;
  dueDate: string;
  serviceDate?: string;
  taxRate?: number;
  taxAmount?: number;
  paidAt?: string;
  finalizedAt?: string;
  finalizedBy?: string;
  lineItems?: Array<{
    description: string;
    quantity: number;
    unitPrice: number;
    total: number;
  }>;
  clientName?: string;
  paymentLinkUrl?: string;
  splitGroupTotal?: number;
  splitPercentage?: number;
  totalPaid?: number;
  balanceDue?: number;
  /**
   * A5c, additive: an ACH clearing against this invoice, in dollars. NOT deducted
   * from `amount` or `balanceDue` (both are the settled plane — INV-89), so the
   * caption must never say "deducted". Absent offline (INVR145).
   */
  processingAmount?: number;
  splitInvoices?: Array<{
    id: string;
    invoiceNumber: string;
    amount: number;
    taxAmount?: number;
    status: string;
    client?: { id: string; name: string };
  }>;
}

interface BillFormData {
  appointmentId?: string;
  // The appointment links the unbilled-items import returned. Separate from the
  // singular `appointmentId` (the picker's one pick) because the import path
  // HIDES the picker (the `!isHorseImport` guard on the "Related Appointment"
  // selector) — before this field existed, the imported ids were destructured in
  // importUnbilledItemsMutation's onSuccess and then dropped, so the appointment
  // was never linked and its fee re-imported forever (S2).
  appointmentIds?: string[];
  horseId: string;
  amount: number;
  description?: string;
  internalNote?: string;
  // "Notes (shown on the invoice)" (ADR-0044); '' is a deleted note.
  clientNote?: string;
  // What the box was filled with. The note is sent only when it differs, so an untouched
  // default is left to the server (it applies the CURRENT one) and a stale or never-loaded
  // copy never overwrites or clears the real note (the web twin of INV-124).
  noteInitial?: string;
  // Editing an older invoice whose note lives in `description`: the box edits that column.
  noteInDescription?: boolean;
  dueDate: Date;
  serviceDate: Date;
  taxRate: number;
  taxAmount: number;
  status?: string;
  lineItems?: LineItem[];
  // BILL-DISC: invoice-level discount (null = none / cleared on save).
  // discountAmount is never sent — the server always recomputes it.
  discountType?: 'percentage' | 'fixed' | null;
  discountValue?: number | null;
  discountReason?: string | null;
  medicationIds?: string[];
  vaccinationIds?: string[];
  procedureIds?: string[];
  // 'owners' | 'third_party' for new invoices;
  // 'current' | 'owners' | 'owner:<name>' | 'third_party' when editing an
  // existing invoice ('owners' on a multi-owner horse re-splits it on save)
  billToType?: string;
  billToName?: string;
  billToEmail?: string;
  billToPhone?: string;
  billToAddress?: string;
}

interface InlinePayment {
  id: string;
  paymentType: string;
  paymentDate: string;
  amount: string;
  referenceNote: string;
}

// The inline Payments rows offer the CLINIC'S types (Settings → Invoicing),
// exactly like the client invoice screen — see `recordablePaymentTypes` below.
// A hardcoded list used to live here; it hid custom types (a clinic's "Venmo")
// and could offer a type the clinic had deactivated, which the backend's
// assertPaymentTypeAllowed then rejected with a 400 on save.

const INLINE_REF_PLACEHOLDERS: Record<string, string> = {
  check: 'Check #',
  credit_card: 'Auth # or last 4',
  debit_card: 'Auth # or last 4',
  write_off: 'Reason for write-off',
  other: 'Description',
};

const initialFormData: BillFormData = {
  appointmentId: '',
  appointmentIds: [],
  horseId: '',
  amount: 0,
  internalNote: '',
  dueDate: addDays(new Date(), 30),
  serviceDate: new Date(),
  taxRate: 0,
  taxAmount: 0,
  status: 'pending',
  lineItems: [],
  discountType: null,
  discountValue: null,
  discountReason: null,
  billToType: 'owners',
  billToName: '',
  billToEmail: '',
  billToPhone: '',
  billToAddress: '',
};

/**
 * The appointment links a save must send: the ids the unbilled-items import
 * returned PLUS the one the picker holds, deduped.
 *
 * Both payload builders use it so create and update can never disagree. NOTE:
 * billing.service.update does NOT link appointments (it ignores appointmentIds
 * entirely) — sending the same shape on update keeps the two builders honest,
 * it is not a claim that editing an invoice re-links anything.
 */
const unionAppointmentIds = (data: BillFormData): string[] =>
  Array.from(
    new Set([
      ...(data.appointmentIds ?? []),
      ...(data.appointmentId ? [data.appointmentId] : []),
    ]),
  );

/**
 * Does this save carry clinical LINKS? NON-EMPTY, never key-presence: both
 * payload builders always send the four keys (as `[]`), so a presence test
 * would route every plain offline invoice create through the online-only path
 * and kill a shipped v3.11.0 capability.
 */
const carriesClinicalLinks = (p: {
  appointmentIds: string[];
  medicationIds: string[];
  vaccinationIds: string[];
  procedureIds: string[];
}): boolean =>
  p.appointmentIds.length > 0 ||
  p.medicationIds.length > 0 ||
  p.vaccinationIds.length > 0 ||
  p.procedureIds.length > 0;

const generateInvoiceNumber = (lastInvoiceNumber?: string) => {
  if (!lastInvoiceNumber) return 'INV-1001';
  
  const number = parseInt(lastInvoiceNumber.split('-')[1]);
  return `INV-${(number + 1).toString().padStart(4, '0')}`;
};

const StatusChip: FC<{ status: string }> = ({ status }) => {
  const getColor = () => {
    switch (status) {
      case 'paid':
        return 'success';
      case 'partially_paid':
        return 'info';
      case 'pending':
      case 'processing':
        return 'warning';
      case 'overdue':
        return 'error';
      default:
        return 'default';
    }
  };

  const getLabel = () => {
    if (status === 'partially_paid') return 'Partially Paid';
    if (status === 'processing') return 'Processing';
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  return (
    <Chip
      label={getLabel()}
      color={getColor()}
      size="small"
    />
  );
};

/**
 * The "Date Range" filter's options, in the order they are offered.
 *
 * The Select used to be wired to NOTHING — `dateRange` was set and never read,
 * so the control said "Last 30 days" over a list of ALL TIME (symptoms I-4, a
 * stated scope that was false, which is symptom S1's class). `findAllPaginated`
 * has accepted `startDate` (an `issuedAt` lower bound) all along.
 *
 * ⚠ The DEFAULT is `allTime`, not the "Last 30 days" this control was drawn with
 * (2026-09-10). The server ANDs the window with the status tab
 * (`{clinicId, status, issuedAt:{gte}}`), and on the clinic's default 30-day
 * terms an invoice is overdue only once it was issued MORE than 30 days ago —
 * exactly outside a 30-day window — so a 30-day default emptied the Overdue tab
 * under a PAST DUE tile that still counted those invoices (symptom S1 re-created
 * on one screen) and dropped everything older than a month off the Paid tab.
 * "All time" keeps the list behaviour the page has always had and makes the
 * control state the truth; narrowing is the user's explicit act. Pinned by
 * `dateRange.test.tsx`'s 60-day Overdue case.
 */
const INVOICE_DATE_RANGES = [
  { value: 'last30', label: 'Last 30 days', days: 30 },
  { value: 'last90', label: 'Last 90 days', days: 90 },
  { value: 'thisYear', label: 'This year', days: null },
  { value: 'allTime', label: 'All time', days: null },
] as const;

/**
 * A preset -> the `issuedAt` window to request.
 *
 * Derived per FETCH rather than memoized into the query key (the key carries the
 * PRESET): a refetch after a create re-derives the boundary, so the invoice the
 * user just made is inside the window the list is supposed to contain it in.
 *
 * ⚠ LOWER BOUND ONLY. An `endDate` of `new Date()` would be the BROWSER's clock
 * bounding a SERVER-generated timestamp (`Invoice.issuedAt` is `@default(now())`
 * and never client-supplied): a client running even slightly behind would post an
 * invoice, refetch on its own invalidation and find `issuedAt > endDate`, i.e.
 * the row it had just created missing from the list in front of it — the same
 * class of symptom this wave closes. Every preset means "up to now" anyway, so
 * the upper bound carried no information to begin with.
 *
 * ⚠ The day boundary is the VIEWER's, not the clinic's. That is right for someone
 * standing in the clinic and off by their own offset for a relief vet in another
 * zone — the same trade the page's other window (`billingDateWindow`, the
 * appointment picker) already makes. Making it a clinic-zone boundary needs a
 * zone -> instant helper the web app does not have (`dateUtils` converts the
 * other way only); a few hours at the edge of a coarse list preset is not worth
 * inventing one. "Last 30 days" means 30 days INCLUDING today.
 */
function invoiceDateWindow(preset: string): { startDate?: string } {
  const now = new Date();
  if (preset === 'thisYear') return { startDate: startOfYear(now).toISOString() };
  const days = INVOICE_DATE_RANGES.find((r) => r.value === preset)?.days;
  // 'allTime' (the default) and any unknown value: no window at all, which is
  // what the default states.
  if (!days) return {};
  return { startDate: startOfDay(subDays(now, days - 1)).toISOString() };
}

const StatCard: FC<{
  title: string;
  value: string;
  /**
   * Tooltip on the VALUE — for a figure that is deliberately absent (offline
   * Past due renders '—'), so hovering says why instead of leaving a dash
   * unexplained.
   */
  valueTitle?: string;
  subtitle?: string;
  /** Secondary legend under the subtitle (A5b: the in-flight ACH). */
  caption?: string;
  captionTitle?: string;
  testId?: string;
  icon: React.ReactNode;
}> = ({ title, value, valueTitle, subtitle, caption, captionTitle, testId, icon }) => (
  <Card
    elevation={0}
    sx={{
      height: '100%',
      borderRadius: 3,
      border: '1px solid',
      borderColor: 'divider',
      boxShadow: 'none',
    }}
  >
    <CardContent sx={{ p: { xs: 1.5, sm: 2.5 }, '&:last-child': { pb: { xs: 1.5, sm: 2.5 } } }}>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start">
        <Box minWidth={0} flex={1}>
          <Typography
            sx={{
              fontSize: '0.7rem',
              fontWeight: 600,
              letterSpacing: '0.5px',
              textTransform: 'uppercase',
              color: 'text.secondary',
              mb: 0.5,
            }}
          >
            {title}
          </Typography>
          <Typography
            component="div"
            data-testid={testId}
            title={valueTitle}
            sx={{
              fontSize: { xs: '1.5rem', sm: '2rem' },
              fontWeight: 700,
              lineHeight: 1.2,
            }}
          >
            {value}
          </Typography>
          {subtitle && (
            <Typography
              variant="body2"
              sx={{
                fontSize: '0.75rem',
                color: 'text.secondary',
                mt: 0.5,
                display: { xs: 'none', sm: 'block' },
              }}
            >
              {subtitle}
            </Typography>
          )}
          {caption && (
            <Typography
              variant="body2"
              title={captionTitle}
              data-testid={testId ? `${testId}-caption` : undefined}
              sx={{ fontSize: '0.72rem', fontWeight: 600, color: 'info.main', mt: 0.25 }}
            >
              {caption}
            </Typography>
          )}
        </Box>
        <Box
          sx={{
            bgcolor: 'rgba(139,94,52,0.08)',
            borderRadius: '50%',
            width: 48,
            height: 48,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            ml: 1,
            color: 'primary.main',
            '& .MuiSvgIcon-root': {
              fontSize: '1.4rem',
            },
          }}
        >
          {icon}
        </Box>
      </Box>
    </CardContent>
  </Card>
);

const Billing: FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm')); // < 600px
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md')); // 600px - 900px
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md')); // < 900px
  const { enqueueSnackbar } = useSnackbar();
  const { trackFeatureUsed } = useAnalytics();
  // The day an INSTANT falls on (an appointment start, `issuedAt`) is the clinic's (DATE-KINDS §2.9).
  const clinicTz = useClinicTimezone();
  // Reactive offline flag for disabling online-only actions (delete, clinical
  // import) — flag-off it is always false.
  const isOffline = useIsOffline();
  // THREE different money gates on this one screen (INV-108): writing the invoice is
  // manage:charges (billing.controller.ts create/update/remove/updateStatus), taking money is
  // manage:payments (payments.controller.ts record), giving it back is manage:refunds. A front
  // desk that may take a cheque need not be able to rewrite the invoice, so the page must not
  // collapse the three into one flag. `InvoiceDetail.tsx` already gates on manage:charges; this
  // keeps the list consistent with it.
  const rbac = useRBAC();
  const can = invoicingControls(rbac);

  const location = useLocation();
  const navigate = useNavigate();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const [formData, setFormData] = useState<BillFormData>(initialFormData);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  // One of INVOICE_DATE_RANGES' keys — the Select below, and the `issuedAt`
  // lower bound every invoice request carries (declared here, above the
  // filter-reset effect that depends on it). `allTime` by default, because the
  // server ANDs the window with the status tab and a 30-day default window
  // excludes the very invoices the Overdue tab is for — see INVOICE_DATE_RANGES.
  const [dateRange, setDateRange] = useState('allTime');
  const [invoicePage, setInvoicePage] = useState(0);
  const invoiceRowsPerPage = 25;

  // Debounce the search input so we only hit the server 300ms after the
  // user stops typing.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm), 300);
    return () => clearTimeout(t);
  }, [searchTerm]);
  // Reset to page 0 whenever a filter changes (server pagination) — the date
  // range included, or narrowing the window while on page 5 asks for page 5 of a
  // shorter list and renders an empty table.
  useEffect(() => {
    setInvoicePage(0);
  }, [debouncedSearch, statusFilter, dateRange]);
  const [invoiceDetailOpen, setInvoiceDetailOpen] = useState(false);
  const [detailInvoice, setDetailInvoice] = useState<Invoice | null>(null);
  const [selectedAppointment, setSelectedAppointment] = useState<Appointment | null>(null);
  const [isHorseImport, setIsHorseImport] = useState(false);
  /**
   * Which deep-link arrival the auto-import effect has already consumed
   * (`horse-<id>` or an appointment id).
   *
   * ⚠ Deliberately NOT reset on dialog close (web minor 13 asked for that;
   * 2026-09-10). The effect's deps include the `invoices` memo and `clinicData`,
   * and a create's own onSuccess invalidates `['invoices']` — so it re-runs
   * moments after a save, with `location.state` STILL carrying
   * `{horseId, autoImportHorse}` (react-router keeps its in-memory copy; the raw
   * `window.history.replaceState` below never reaches it). Clearing the ref
   * therefore re-OPENS the dialog and fires a second import right after every
   * patient-file save — proven by mutation, and pinned by "does not re-open and
   * re-import after the save it was opened for" in `horseImportLinks.test.tsx`.
   * The symptom the minor describes (coming back for the same horse imports
   * nothing) is not reachable: the patient file is a different ROUTE
   * (`/patients/:horseId`), so this page unmounts and the ref is recreated on
   * every arrival.
   */
  const hasImportedRef = useRef<string | null>(null);
  const hasAutoAddedPaymentRow = useRef(false);
  const hasManuallyEditedPayment = useRef(false);
  
  // Filter by client or horse (from URL params)
  const [clientFilter, setClientFilter] = useState<string | null>(null);
  const [horseFilter, setHorseFilter] = useState<string | null>(null);

  // Payment collection state
  const [clientInvoiceWizardOpen, setClientInvoiceWizardOpen] = useState(false);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [pendingInvoiceForPayment, setPendingInvoiceForPayment] = useState<Invoice | null>(null);
  const [paymentDialogCardOnly, setPaymentDialogCardOnly] = useState(false);
  const [pendingCardAmount, setPendingCardAmount] = useState<number | undefined>(undefined);
  const [stripeConnectEnabled, setStripeConnectEnabled] = useState(false);
  const [inlinePayments, setInlinePayments] = useState<InlinePayment[]>([]);
  const [paymentLinkDialogOpen, setPaymentLinkDialogOpen] = useState(false);
  const [paymentLinkInvoice, setPaymentLinkInvoice] = useState<Invoice | null>(null);
  const [paymentLinkAmount, setPaymentLinkAmount] = useState<number | undefined>(undefined);

  // Bulk selection state
  const [selectedInvoiceIds, setSelectedInvoiceIds] = useState<Set<string>>(new Set());
  // Split button (New Invoice dropdown)
  const [splitBtnOpen, setSplitBtnOpen] = useState(false);
  const splitBtnRef = useRef<HTMLDivElement>(null);
  // Bulk Record Payment(s) modal
  const [bulkPayModalOpen, setBulkPayModalOpen] = useState(false);
  const [bulkPayRefs, setBulkPayRefs] = useState<Record<string, string>>({});
  const [bulkPayType, setBulkPayType] = useState('');
  const [bulkPayDate, setBulkPayDate] = useState(() => todayLocalISO());
  const [bulkPaySubmitting, setBulkPaySubmitting] = useState(false);

  const queryClient = useQueryClient();

  // Normalise invoices coming back from either the paginated envelope or
  // the legacy array — both can have stringified JSON columns depending
  // on the Prisma driver version. Centralised here so we parse once.
  const normaliseInvoice = (invoice: any): Invoice => ({
    ...invoice,
    lineItems:
      typeof invoice.lineItems === 'string'
        ? JSON.parse(invoice.lineItems)
        : invoice.lineItems || [],
    horse: {
      ...invoice.horse,
      owners:
        typeof invoice.horse?.owners === 'string'
          ? JSON.parse(invoice.horse.owners)
          : Array.isArray(invoice.horse?.owners)
          ? invoice.horse.owners
          : [],
    },
  });

  // Clinic-wide invoice stats — computed server-side via a single groupBy.
  // Previously the page summed across every invoice in JS, which was a
  // top cause of the "show nothing with lots of data" hang on clinics with
  // thousands of invoices.
  const { data: clinicStats } = useQuery(
    ['billing-stats'],
    withOfflineFallback(
      () => billingApi.getClinicStats(),
      async () => {
        // Compose the ClinicInvoiceStats shape from the replica.
        // readInvoiceStats nets succeeded+processing out of OPEN rows and
        // leaves `paid` gross, so these are the same two planes the server
        // returns. The one thing the replica cannot reproduce is the PAST-DUE
        // split (the server derives it from the clinic's calendar day), so the
        // whole open balance reads as Outstanding and Past due reads 0 rather
        // than a guess the clinic would act on.
        const paidSince = new Date(Date.now() - 30 * 86_400_000);
        const [open, paid, paid30, all] = await Promise.all([
          readInvoiceStats([...OPEN_INVOICE_STATUSES]),
          readInvoiceStats('paid'),
          readInvoiceStats('paid', { paidSince }),
          readInvoiceStats(),
        ]);
        return {
          totalRevenue: paid.total,
          pendingAmount: open.total,
          overdueAmount: 0,
          averageInvoice: paid.count > 0 ? paid.total / paid.count : 0,
          // `owedCount`, not `count`: the server's open counts are
          // COUNT(*) FILTER (WHERE owed > 0), so a fully-paid row that still
          // carries an open status is excluded there and must be here too.
          // `totalCount` below stays on `count` — the server counts every row.
          pendingCount: open.owedCount,
          overdueCount: 0,
          totalCount: all.count,
          paidAmount30Days: paid30.total,
          paidCount30Days: paid30.count,
          outstandingAmount: open.total,
          pastDueAmount: 0,
          processingAmount: open.processing,
          openInvoiceCount: open.owedCount,
        };
      },
    ),
    { staleTime: 60_000 },
  );

  // Paginated invoices list. Previously the page fetched every invoice
  // and did client-side filtering + pagination. Now the server handles it.
  // `dateRange` (the preset, not the derived dates) is part of the key: a window
  // outside it would leave React Query serving the cached page under the previous
  // scope, i.e. the list disagreeing with its own stated range again. Both arms
  // derive the window from the same helper, so online and offline agree.
  const { data: invoicesPage, isLoading: isLoadingInvoices } = useQuery(
    ['invoices', { page: invoicePage + 1, limit: invoiceRowsPerPage, status: statusFilter, search: debouncedSearch, horseFilter, clientFilter, dateRange }],
    withOfflineFallback(
      () =>
        billingApi.listInvoicesPaginated({
          page: invoicePage + 1,
          limit: invoiceRowsPerPage,
          status: statusFilter === 'all' ? undefined : statusFilter,
          search: debouncedSearch || undefined,
          horseId: horseFilter || undefined,
          clientId: clientFilter || undefined,
          ...invoiceDateWindow(dateRange),
        }),
      () =>
        readInvoicesPage({
          page: invoicePage + 1,
          limit: invoiceRowsPerPage,
          status: statusFilter === 'all' ? undefined : statusFilter,
          search: debouncedSearch || undefined,
          horseId: horseFilter || undefined,
          clientId: clientFilter || undefined,
          ...invoiceDateWindow(dateRange),
        }) as any,
    ),
    {
      staleTime: 30_000,
      keepPreviousData: true,
    },
  );
  const invoices: Invoice[] = useMemo(
    () => (invoicesPage?.data ?? []).map(normaliseInvoice),
    [invoicesPage],
  );
  const totalInvoices = invoicesPage?.total ?? 0;

  // Scope appointments to a reasonable recent+upcoming window. Billing
  // mostly needs appointments the user might attach an invoice to, not
  // every historical appointment in the clinic.
  const billingDateWindow = useMemo(() => {
    const now = new Date();
    const sixtyDaysAgo = new Date(now);
    sixtyDaysAgo.setDate(now.getDate() - 60);
    const thirtyDaysAhead = new Date(now);
    thirtyDaysAhead.setDate(now.getDate() + 30);
    return {
      dateFrom: sixtyDaysAgo.toISOString(),
      dateTo: thirtyDaysAhead.toISOString(),
    };
  }, []);
  const { data: appointments = [] } = useQuery<Appointment[]>(
    ['billing-appointments', billingDateWindow],
    async () => {
      const response = await api.get('/appointments', { params: billingDateWindow });
      return response.data;
    },
    // Feeds the appointment AUTO-LINK picker, which is online-only by design —
    // skip the doomed fetch offline instead of error-retrying.
    { staleTime: 60_000, enabled: skipWhenOffline() },
  );

  // Horses list — legacy endpoint returns up to DEFAULT_LIST_CAP (500) rows.
  // Used for the horse autocomplete on invoice creation. Good enough for
  // most clinics; if a tenant exceeds the cap we'll switch this to a
  // server-side search autocomplete.
  const { data: horses = [] } = useQuery<Horse[]>(
    'horses',
    // Offline: serve the patient picker from the replica (same 500-row cap), so
    // offline invoice creation doesn't dead-end on an empty horse Select.
    withOfflineFallback(
      async () => (await api.get('/horses')).data,
      () => readHorsesPage({ page: 1, limit: 500 }).then((p) => p.data as Horse[]),
    ),
    { staleTime: 60_000 },
  );

  // Fetch clinic data for default tax rate
  // Fresh on mount, as before: the invoice dialogs prefill the tax rate and terms from it. The key is
  // warmed app-wide, so wait on the refetch (isFetching), not just the first load.
  const { data: clinicData, isFetching: isLoadingClinic } = useClinicRecord({ staleTime: 0 });

  // The note every new invoice starts with (ADR-0044). Online only: offline it stays unknown
  // and the server fills it when the queued invoice syncs.
  const { data: invoiceSettings, refetch: refetchInvoiceSettings } = useQuery<InvoiceSettings>(
    ['invoice-settings'],
    () => invoiceSettingsApi.get(),
    { staleTime: 60_000 },
  );

  // Fetch service catalog for line item autocomplete
  const { data: catalogItems = [] } = useQuery<ServiceCatalogItem[]>(
    'service-catalog',
    () => serviceCatalogService.getAll()
  );

  // Clinic items + global items without a clinic override, for the autocomplete dropdown
  const clinicCatalogOptions: CatalogOption[] = useMemo(() => {
    const clinicOwned = catalogItems.filter(item => item.clinicId && item.isActive);
    const clinicKeys = new Set(clinicOwned.map(i => `${i.category}-${i.name.toLowerCase()}`));
    const globalDefaults = catalogItems.filter(
      item => !item.clinicId && item.isActive && !clinicKeys.has(`${item.category}-${item.name.toLowerCase()}`)
    );
    return [...clinicOwned, ...globalDefaults].map(item => ({
      id: item.id,
      name: item.name,
      category: item.category,
      basePrice: item.basePrice,
      unit: item.unit,
      aliases: item.aliases,
    }));
  }, [catalogItems]);

  // Fetch client data when filtering by client
  const { data: filterClient } = useQuery<Client>(
    ['client', clientFilter],
    () => clientApi.get(clientFilter!),
    { enabled: !!clientFilter }
  );

  // The clinic's configured payment types (Settings → Invoicing) — the same
  // query key and endpoint the client invoice screen reads, so both screens
  // offer the same methods including custom ones like "Venmo".
  const paymentTypesQuery = useQuery<PaymentTypeOption[]>(
    ['payment-types'],
    () => api.get('/payment-types').then((r) => r.data),
    { staleTime: 60_000 },
  );
  const paymentTypes = paymentTypesQuery.data ?? [];
  // 'loading' / 'error' → keep the built-in fallback; 'loaded' + empty list →
  // the clinic really has no active type and nothing here may be recorded.
  const paymentTypesState = paymentTypesLoadState(paymentTypesQuery);
  // Both record-only method selects on this screen — the bulk "Record
  // Payment(s)" modal and the invoice dialog's inline Payments rows — offer
  // every active Settings method, including card types, for cards charged on
  // an external terminal. They only RECORD (never charge); the caption in the
  // bulk modal says so. Only the Stripe payment-link flow and the reserved
  // account-credit value stay excluded (utils/paymentTypes.ts).
  const recordablePaymentTypes = useMemo(
    () => recordablePaymentTypeOptions(paymentTypes, paymentTypesState),
    [paymentTypes, paymentTypesState],
  );
  // The clinic deactivated every payment type (GET /payment-types is
  // active-only). Offering the built-ins here would hand the user four methods
  // the server's assertPaymentTypeAllowed rejects, so the record flows go
  // read-only instead and point at Settings.
  const noRecordablePaymentTypes = recordablePaymentTypes.length === 0;
  const NO_PAYMENT_TYPES_HELP =
    'This clinic has no active payment types. Add or reactivate one under Settings → Invoicing before recording a payment.';
  const defaultInlinePaymentType = useMemo(
    () => defaultRecordablePaymentType(recordablePaymentTypes),
    [recordablePaymentTypes],
  );
  // A row can outlive the option it holds: it may have been created from the
  // static fallback while GET /payment-types was still in flight (a deep-linked
  // ?editMode dialog), or the clinic may deactivate a type in Settings mid-edit.
  // Either way the select would render blank and the save would 400 on an
  // unknown type, so re-snap those rows to the default. Only once the real list
  // is in hand — the fallback must never overwrite a clinic-configured choice.
  useEffect(() => {
    if (paymentTypes.length === 0) return;
    const offered = new Set([...recordablePaymentTypes.map((t) => t.value), 'payment_link']);
    setInlinePayments((prev) =>
      prev.some((p) => !offered.has(p.paymentType))
        ? prev.map((p) =>
            offered.has(p.paymentType) ? p : { ...p, paymentType: defaultInlinePaymentType },
          )
        : prev,
    );
  }, [paymentTypes, recordablePaymentTypes, defaultInlinePaymentType]);

  // Check Stripe Connect status
  useEffect(() => {
    const fetchStripeStatus = async () => {
      try {
        const response = await api.get('/stripe-connect/status');
        setStripeConnectEnabled(response.data.connected && response.data.chargesEnabled);
      } catch (error) {
        console.error('Failed to fetch Stripe status:', error);
        setStripeConnectEnabled(false);
      }
    };
    fetchStripeStatus();
  }, []);

  // Parse URL params for filtering
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const clientId = params.get('clientId');
    const horseId = params.get('horseId');
    
    if (clientId) {
      setClientFilter(clientId);
      setHorseFilter(null);
    } else if (horseId) {
      setHorseFilter(horseId);
      setClientFilter(null);
    }
  }, [location.search]);

  // Handle Stripe Checkout return URL params (?payment=success|cancelled)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const paymentStatus = params.get('payment');
    const invoiceNumber = params.get('invoice');

    if (paymentStatus === 'success') {
      enqueueSnackbar(
        invoiceNumber
          ? `Payment received for Invoice ${invoiceNumber}!`
          : 'Payment received successfully!',
        { variant: 'success' },
      );
      queryClient.invalidateQueries(['invoices']); queryClient.invalidateQueries(['billing-stats']);
      params.delete('payment');
      params.delete('invoice');
      navigate({ search: params.toString() }, { replace: true });
    } else if (paymentStatus === 'cancelled') {
      enqueueSnackbar(
        invoiceNumber
          ? `Payment was cancelled for Invoice ${invoiceNumber}`
          : 'Payment was cancelled',
        { variant: 'info' },
      );
      params.delete('payment');
      params.delete('invoice');
      navigate({ search: params.toString() }, { replace: true });
    }
  }, []);

  // Get the name for the filter chip display
  const filterDisplayName = useMemo(() => {
    if (horseFilter) {
      const horse = horses.find(h => h.id === horseFilter);
      return horse ? `Patient: ${horse.name}` : 'Patient';
    }
    if (clientFilter && filterClient) {
      return `Client: ${filterClient.name}`;
    }
    return '';
  }, [horseFilter, clientFilter, horses, filterClient]);

  // Clear filter and navigate to clean URL
  const handleClearFilter = () => {
    setClientFilter(null);
    setHorseFilter(null);
    navigate('/billing', { replace: true });
  };

  // Stats come from the server (`/billing/stats/clinic`). The open figures are
  // the CANONICAL plane: open statuses incl. partially_paid, succeeded and
  // processing netted out, past due derived from dueDate in the clinic's zone.
  // `?? pendingAmount/overdueAmount` covers a response cached before the
  // additive keys existed — they carry the identical values server-side.
  // Falls back to zeros while the query is in flight.
  const stats = useMemo(
    () => ({
      totalRevenue: clinicStats?.totalRevenue ?? 0,
      outstandingAmount: clinicStats?.outstandingAmount ?? clinicStats?.pendingAmount ?? 0,
      pastDueAmount: clinicStats?.pastDueAmount ?? clinicStats?.overdueAmount ?? 0,
      processingAmount: clinicStats?.processingAmount ?? 0,
      averageInvoice: clinicStats?.averageInvoice ?? 0,
      pendingCount: clinicStats?.pendingCount ?? 0,
      overdueCount: clinicStats?.overdueCount ?? 0,
      totalCount: clinicStats?.totalCount ?? 0,
      paidAmount30Days: clinicStats?.paidAmount30Days ?? 0,
      paidCount30Days: clinicStats?.paidCount30Days ?? 0,
    }),
    [clinicStats],
  );

  // A3: BILLED appointments stay in the list and are rendered DISABLED with
  // their invoice number (see the Select below) rather than vanishing — a
  // silently-missing row read as "the app lost my appointment", and the
  // backend already rejects a re-bill with the invoice number anyway. Only
  // horse-less rows (owner/barn-subject bookings) are still excluded:
  // invoicing from an appointment is per-patient (Invoice.horseId is required,
  // ADR-0026) and the option label reads appointment.horse.name.
  const availableAppointments = useMemo(() => {
    // A cancelled visit is not billable (the server refuses it too).
    return appointments.filter(apt => apt.horseId && apt.horse && !isCancelled(apt.status));
  }, [appointments]);

  // The server filters by status / search / horseId / clientId, so the rendered
  // list is just the current page. The legacy "match invoices by horse
  // ownership" fallback is BACK, server-side: `?clientId=` now means membership
  // (direct clientId OR a legacy clientId-null invoice for a horse this client
  // owns, third-party-billed excluded) — billing/client-invoice-scope.ts, the
  // same predicate the client's balance and Open-Invoices count use.
  const filteredInvoices = invoices;

  // ── Bulk selection helpers ────────────────────────────────────────────────
  function toggleInvoiceSelection(id: string) {
    setSelectedInvoiceIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  const OPEN_STATUSES = ['pending', 'overdue', 'partially_paid'];

  function toggleAllInvoices() {
    const openIds = filteredInvoices.filter((i) => OPEN_STATUSES.includes(i.status)).map((i) => i.id);
    const allSelected = openIds.length > 0 && openIds.every((id) => selectedInvoiceIds.has(id));
    setSelectedInvoiceIds((prev) => {
      const next = new Set(prev);
      if (allSelected) openIds.forEach((id) => next.delete(id));
      else openIds.forEach((id) => next.add(id));
      return next;
    });
  }

  async function handleBulkConfirm() {
    const selectedInvs = filteredInvoices.filter((i) => selectedInvoiceIds.has(i.id));
    setBulkPaySubmitting(true);
    try {
      await batchPaymentApi.record({
        paymentType: bulkPayType,
        // Same anchor as every other payment-date post (DATE-KINDS §2.5 W6): the
        // picker is `type="date"`, so this is a bare `yyyy-MM-dd` and the server
        // used to store it at UTC midnight, i.e. the PREVIOUS day for every US
        // clinic that reads it back.
        paymentDate: ymdToNoonUtcIso(bulkPayDate) || undefined,
        allocations: selectedInvs.map((inv) => ({
          invoiceId: inv.id,
          amount: inv.balanceDue ?? Math.max(0, (inv.amount ?? 0) + (inv.taxAmount ?? 0) - (inv.totalPaid ?? 0)),
          referenceNote: bulkPayRefs[inv.id]?.trim() || undefined,
        })),
      });
      enqueueSnackbar(`${selectedInvs.length} invoice${selectedInvs.length > 1 ? 's' : ''} marked as paid`, { variant: 'success' });
      setBulkPayModalOpen(false);
      setSelectedInvoiceIds(new Set());
      setBulkPayRefs({});
      queryClient.invalidateQueries(['invoices']);
      queryClient.invalidateQueries(['billing-stats']);
    } catch (err: any) {
      enqueueSnackbar(err?.response?.data?.message ?? 'Failed to record payments', { variant: 'error' });
    } finally {
      setBulkPaySubmitting(false);
    }
  }

  const createInvoice = useMutation(
    async (data: BillFormData) => {
      // The note box is the client note (ADR-0044): `description` is never the web's to send here.
      const {
        billToType, billToName, billToEmail, billToPhone, billToAddress,
        noteInDescription, noteInitial, description, clientNote, ...rest
      } = data;
      const payload = {
        ...rest,
        // Only a note staff changed: an untouched default is left to the server, which starts the
        // invoice with the clinic's CURRENT default (INV-123).
        ...(clientNote !== noteInitial && { clientNote: clientNote ?? '' }),
        dueDate: format(data.dueDate, "yyyy-MM-dd'T'12:00:00.000'Z'"),
        serviceDate: format(data.serviceDate, "yyyy-MM-dd'T'12:00:00.000'Z'"),
        taxRate: data.taxRate,
        taxAmount: data.taxAmount,
        lineItems: data.lineItems || [],
        appointmentIds: unionAppointmentIds(data),
        medicationIds: data.medicationIds || [],
        vaccinationIds: data.vaccinationIds || [],
        procedureIds: data.procedureIds || [],
        billToThirdParty: billToType === 'third_party' && billToName?.trim()
          ? {
              name: billToName.trim(),
              email: billToEmail?.trim() || undefined,
              phone: billToPhone?.trim() || undefined,
              address: billToAddress?.trim() || undefined,
            }
          : undefined,
      };

      // Link-carrying creates are STRICTLY online-only. /sync/push strips all
      // four id arrays on apply (they are not Invoice columns —
      // stripToModelColumns, sync.service.ts:271), so a queued create would
      // land as a LINKLESS invoice: the charges billed, the appointment /
      // medication / vaccination / procedure rows still unbilled and re-offered
      // by the next import. That is the S2 double-charge, one sync later.
      // Same shape as updateInvoice's billToOwners bypass further down this file
      // (`if (payload.billToOwners) { return networkFn(); }`, with its own
      // rationale comment): never hand it to executeWrite, whose
      // connectivity-error fallthrough would enqueue a doomed op.
      if (carriesClinicalLinks(payload)) {
        if (!skipWhenOffline()) {
          throw new OfflineWriteBlockedError(
            'Linked charges need a connection — reconnect and save again; nothing has been saved.',
          );
        }
        return api.post('/billing', payload).then((r) => r.data);
      }

      // Offline-capable: capture the invoice (manual line items) into the queue.
      // The unique invoiceNumber is allocated server-side on sync; the
      // appointment/med/etc. relation links + the double-bill guard remain
      // online-only (those fields are stripped on the sync apply path), so an
      // offline invoice is the manual line-items kind. Payment stays online.
      // NOTE: multi-owner SPLIT (one invoice per owner) is online-only too — an
      // offline invoice is single/unsplit (total is correct; per-owner structure
      // is reconstructed by re-issuing online if needed).
      return executeWrite({
        entityType: 'invoice',
        table: 'invoices',
        payload,
        // Stamp server-defaulted DISPLAY fields onto the optimistic row ONLY (never
        // the queued op — the server sets its own issuedAt/status on create) so the
        // offline invoice list/detail render before sync (they format issuedAt; a
        // missing value crashes the Billing screen).
        extra: { issuedAt: new Date().toISOString(), status: 'pending' },
        networkFn: () => api.post('/billing', payload).then((r) => r.data),
      });
    },
    {
      onSuccess: (createdInvoice) => {
        queryClient.invalidateQueries(['invoices']);
        queryClient.invalidateQueries(['billing-stats']);
        // A3: creating an invoice LINKS its appointment(s) server-side. The
        // picker query is ['billing-appointments', window] with staleTime 60s,
        // refetchOnWindowFocus off, and this page never unmounts — without
        // this the picker kept offering the appointment it just billed, and
        // the next save 400d "appointment(s) already invoiced".
        queryClient.invalidateQueries(['billing-appointments']);
        return createdInvoice;
      },
    }
  );

  const updateInvoice = useMutation(
    async ({ id, data }: { id: string; data: BillFormData }) => {
      // billToType 'current' keeps the existing payer; 'owner:<name>',
      // 'owners' (re-split between co-owners) or 'third_party' switches it.
      const {
        billToType, billToName, billToEmail, billToPhone, billToAddress,
        noteInDescription, noteInitial, description, clientNote, ...payerStripped
      } = data;
      const payload = {
        ...payerStripped,
        // ADR-0044: only a note staff changed is sent (a stale or never-loaded copy must not
        // overwrite or clear the real one), to the column it lives in; '' clears it.
        ...(clientNote !== noteInitial &&
          (noteInDescription ? { description: clientNote ?? '' } : { clientNote: clientNote ?? '' })),
        billToOwnerName: billToType?.startsWith('owner:') ? billToType.slice('owner:'.length) : undefined,
        billToOwners: billToType === 'owners' ? true : undefined,
        billToThirdParty: billToType === 'third_party' && billToName?.trim()
          ? {
              name: billToName.trim(),
              email: billToEmail?.trim() || undefined,
              phone: billToPhone?.trim() || undefined,
              address: billToAddress?.trim() || undefined,
            }
          : undefined,
        dueDate: format(data.dueDate, "yyyy-MM-dd'T'12:00:00.000'Z'"),
        serviceDate: format(data.serviceDate, "yyyy-MM-dd'T'12:00:00.000'Z'"),
        taxRate: data.taxRate,
        taxAmount: data.taxAmount,
        lineItems: data.lineItems || [],
        appointmentIds: unionAppointmentIds(data),
        medicationIds: data.medicationIds || [],
        vaccinationIds: data.vaccinationIds || [],
        procedureIds: data.procedureIds || [],
      };
      const networkFn = () => api.put(`/billing/${id}`, payload).then((r) => r.data);
      // I-8: EVERY payer switch is STRICTLY online-only — not just the owners
      // re-split. /sync/push refuses all three keys on apply: `billToOwners`
      // outright, and `billToThirdParty` / `billToOwnerName` through
      // `assertPayerSwitchAllowed` (backend/src/common/payer-switch-guard.ts,
      // mirrored onto the sync UPDATE arm by B4). None of the three is an
      // `Invoice` COLUMN either, so executeUpdate's optimistic merge writes
      // nothing a reader can see: the vet got a success toast, the Bill To card
      // kept showing the OLD payer, and the refusal surfaced hours later as a
      // "Failed" row in Review unsynced changes. So never hand a switch to
      // executeUpdate, whose connectivity-error fallthrough would enqueue that
      // doomed op — refuse it in the create path's words instead (the linked-
      // charges bypass above). A VALUE, never key presence: the builder always
      // sends the three keys, so `'billToThirdParty' in payload` would route
      // every offline invoice edit through the online-only path.
      if (payload.billToOwners || payload.billToThirdParty || payload.billToOwnerName) {
        if (!skipWhenOffline()) {
          throw new OfflineWriteBlockedError(
            'Changing who an invoice is billed to needs a connection — reconnect and save again; nothing has been saved.',
          );
        }
        return networkFn();
      }
      // Offline-capable (see createInvoice).
      return executeUpdate({
        entityType: 'invoice',
        table: 'invoices',
        entityId: id,
        payload,
        networkFn,
      });
    },
    {
      onSuccess: (updatedInvoice) => {
        trackFeatureUsed('invoice_updated');
        queryClient.invalidateQueries(['invoices']); queryClient.invalidateQueries(['billing-stats']);
        queryClient.invalidateQueries(['billing-appointments']); // A3 — see createInvoice
        return updatedInvoice;
      },
    }
  );

  const deleteInvoice = useMutation(
    async (id: string) => {
      await api.delete(`/billing/${id}`);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['invoices']); queryClient.invalidateQueries(['billing-stats']);
        // A3: billing.service's remove() clears Appointment.invoiceId inside its
        // tx, so the appointment becomes billable again — the picker must hear
        // about it. (voidInvoice does NOT un-link, so the void path needs no
        // invalidation.)
        queryClient.invalidateQueries(['billing-appointments']);
      },
      onError: (error: any) => {
        enqueueSnackbar(error.response?.data?.message || 'Failed to delete invoice', { variant: 'error' });
      },
    }
  );

  // Fetch clinical summary and service catalog for pricing lookup
  const fetchClinicalSummaryWithPricing = async (appointmentId: string) => {
    const [clinicalResponse, catalogResponse] = await Promise.all([
      api.get(`/appointments/${appointmentId}/clinical-summary`),
      api.get('/service-catalog')
    ]);
    return {
      clinical: clinicalResponse.data,
      catalog: catalogResponse.data
    };
  };

  const importClinicalDataMutation = useMutation(
    (appointmentId: string) => fetchClinicalSummaryWithPricing(appointmentId),
    {
      onSuccess: (data) => {
        const { clinical, catalog } = data;
        const { medications, vaccinations, procedures } = clinical;
        const newLineItems: LineItem[] = [];
        let itemsImported = 0;
        let itemsWithoutPrice = 0;
        
        // Track IDs of items being imported
        const medicationIds: string[] = [];
        const vaccinationIds: string[] = [];
        const procedureIds: string[] = [];

        // Build catalog arrays partitioned by ownership for 3-tier matching
        const clinicItems = catalog.filter((item: any) => item.clinicId && item.isActive);
        const globalItems = catalog.filter((item: any) => !item.clinicId && item.isActive);

        // 3-tier lookup mirroring backend findByName: exact name → alias → contains
        const findCatalogPrice = (name: string, category: string): number => {
          const nameLower = name.toLowerCase().trim();
          const items = [...clinicItems, ...globalItems]; // clinic-first priority

          // Tier 1: exact name match (case-insensitive)
          const exactMatch = items.find(
            (item: any) => item.category === category && item.name.toLowerCase().trim() === nameLower
          );
          if (exactMatch) return exactMatch.basePrice;

          // Tier 2: alias match (case-insensitive)
          const aliasMatch = items.find((item: any) => {
            if (item.category !== category) return false;
            const aliases: string[] = Array.isArray(item.aliases) ? item.aliases : [];
            return aliases.some((a: string) => a.toLowerCase().trim() === nameLower);
          });
          if (aliasMatch) return aliasMatch.basePrice;

          // Tier 3: word-boundary contains match (prevents false positives)
          const containsMatch = items.find((item: any) => {
            if (item.category !== category) return false;
            const catalogName = item.name.toLowerCase().trim();
            if (catalogName === nameLower) return false; // Already checked in Tier 1
            const escaped = catalogName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            const wordBoundary = new RegExp(`(^|[\\s\\-\\(,/])${escaped}([\\s\\-\\),/]|$)`);
            return wordBoundary.test(nameLower);
          });
          if (containsMatch) return containsMatch.basePrice;

          return 0;
        };

        // Add medications (all unbilled ones, even without price)
        medications.forEach((med: any) => {
          // Skip if already invoiced
          if (med.invoiceId) {
            return;
          }
          
          let unitPrice = med.unitPrice;
          
          // If no price on the item, look up in catalog (3-tier matching)
          if (!unitPrice || unitPrice === 0) {
            unitPrice = findCatalogPrice(med.name, 'medication');
          }

          // The editor's own line math, so the total is rounded to the cent (INV-112).
          newLineItems.push(applyLineDiscount({
            description: `${med.name}${med.dosage ? ` (${med.dosage})` : ''}`,
            quantity: med.quantity || 1,
            unitPrice: unitPrice || 0,
            total: 0,
            category: 'medication',
          }));
          medicationIds.push(med.id);
          itemsImported++;
          
          if (!unitPrice || unitPrice === 0) {
            itemsWithoutPrice++;
          }
        });

        // Add vaccinations (all unbilled ones, even without price)
        vaccinations.forEach((vac: any) => {
          // Skip if already invoiced
          if (vac.invoiceId) {
            return;
          }
          
          let unitPrice = vac.unitPrice;
          
          // If no price on the item, look up in catalog (3-tier matching)
          if (!unitPrice || unitPrice === 0) {
            unitPrice = findCatalogPrice(vac.vaccineName, 'vaccination');
          }

          // The recorded dose, else one (ADR-0038), through the editor's line math (INV-112).
          newLineItems.push(applyLineDiscount({
            description: `${vac.vaccineName}`,
            quantity: vac.quantity || 1,
            unitPrice: unitPrice || 0,
            total: 0,
            category: 'vaccination',
          }));
          vaccinationIds.push(vac.id);
          itemsImported++;
          
          if (!unitPrice || unitPrice === 0) {
            itemsWithoutPrice++;
          }
        });

        // Add procedures (all unbilled ones, even without price)
        procedures.forEach((proc: any) => {
          // Skip if already invoiced
          if (proc.invoiceId) {
            return;
          }
          
          let unitPrice = proc.unitPrice;
          
          // If no price on the item, look up in catalog (3-tier matching)
          if (!unitPrice || unitPrice === 0) {
            unitPrice = findCatalogPrice(proc.name, 'procedure');
          }

          newLineItems.push({
            description: `${proc.name}`,
            quantity: 1,
            unitPrice: unitPrice || 0,
            total: unitPrice || 0,
            category: 'procedure',
          });
          procedureIds.push(proc.id);
          itemsImported++;
          
          if (!unitPrice || unitPrice === 0) {
            itemsWithoutPrice++;
          }
        });

        // Update form data with imported clinical data and track billed item IDs
        setFormData(prev => {
          const allItems = [...(prev.lineItems || []), ...newLineItems];
          const { taxAmount, amount } = recalcAmountWithTax(allItems, prev.taxRate, formInvoiceDiscount(prev));
          return {
            ...prev,
            lineItems: allItems,
            medicationIds: [...(prev.medicationIds || []), ...medicationIds],
            vaccinationIds: [...(prev.vaccinationIds || []), ...vaccinationIds],
            procedureIds: [...(prev.procedureIds || []), ...procedureIds],
            taxAmount,
            amount,
          };
        });
        
        // Always show notification when items are imported
        if (itemsImported > 0) {
          if (itemsWithoutPrice > 0) {
            enqueueSnackbar(`Imported ${itemsImported} item(s). ${itemsWithoutPrice} item(s) have no price set — add prices before finalizing.`, { variant: 'warning' });
          } else {
            enqueueSnackbar(`Imported ${itemsImported} item(s) from clinical data. All items have prices set.`, { variant: 'success' });
          }
        }
      },
      onError: (error: any) => {
        enqueueSnackbar(error.response?.data?.message || 'Failed to import clinical data', { variant: 'error' });
      },
    }
  );


  const handleOpenDialog = (invoice?: Invoice) => {
    setIsHorseImport(false);
    if (invoice) {
      setSelectedInvoice(invoice);
      // ADR-0044: the box shows the note the PDF prints. An older web invoice (no client note,
      // a description) keeps editing its description, so its statement line follows the edit.
      const noteInDescription = !invoice.clientNote?.trim() && !!invoice.description?.trim();
      const note = (noteInDescription ? invoice.description : invoice.clientNote) ?? '';
      setFormData({
        appointmentId: invoice.appointments?.[0]?.id || '',
        horseId: invoice.horseId,
        amount: invoice.amount,
        clientNote: note,
        noteInitial: note,
        noteInDescription,
        dueDate: parseISO(invoice.dueDate.substring(0, 10)),
        // No service date: seed the day InvoiceDetail shows, the appointment's, else the issue day.
        serviceDate: parseISO(invoice.serviceDate
          ? invoice.serviceDate.substring(0, 10)
          : formatInstantInZone(invoice.appointments?.[0]?.date || invoice.issuedAt, clinicTz, 'yyyy-MM-dd')),
        taxRate: invoice.taxRate || 0,
        taxAmount: invoice.taxAmount || 0,
        status: invoice.status,
        lineItems: invoice.lineItems || [],
        discountType: (invoice as any).discountType || null,
        discountValue: (invoice as any).discountValue ?? null,
        discountReason: (invoice as any).discountReason || null,
        billToType: 'current',
        billToName: '',
        billToEmail: '',
        billToPhone: '',
        billToAddress: '',
      });
    } else {
      const days = clinicData?.defaultPaymentDays ?? 30;
      setSelectedInvoice(null);
      setFormData({
        ...initialFormData,
        taxRate: clinicData?.taxRate || 0,
        dueDate: addDays(new Date(), days),
      });
    }
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setSelectedInvoice(null);
    setIsHorseImport(false);
    setFormData(initialFormData);
    setInlinePayments([]);
    hasAutoAddedPaymentRow.current = false;
    hasManuallyEditedPayment.current = false;
  };

  // ADR-0044: every way of starting a new invoice resets the form with the note unset; it then
  // shows the clinic's default, re-read as the dialog opens, until staff change the note.
  useEffect(() => {
    if (isDialogOpen && !selectedInvoice) void refetchInvoiceSettings();
  }, [isDialogOpen, selectedInvoice, refetchInvoiceSettings]);
  useEffect(() => {
    if (!isDialogOpen || selectedInvoice || !invoiceSettings) return;
    if (formData.clientNote !== formData.noteInitial) return; // staff changed it
    const next = invoiceSettings.defaultClientNote ?? '';
    if (formData.clientNote === next) return;
    setFormData((prev) => ({ ...prev, clientNote: next, noteInitial: next }));
  }, [isDialogOpen, selectedInvoice, invoiceSettings, formData.clientNote, formData.noteInitial]);

  // Auto-add a default payment row when line items first appear.
  // Use formData.amount + taxAmount (which accounts for split invoice proportions).
  // For new invoices where amount is computed from lineItems, both update in the same render.
  useEffect(() => {
    const invoiceTotal = (formData.amount || 0) + (formData.taxAmount || 0);
    // Skip auto-adding payment row for new split invoices (payments collected per split invoice)
    const horse = horses.find(h => h.id === formData.horseId);
    const willSplit = !selectedInvoice && horse && billableOwnersOf(horse.owners).length > 1;
    if (
      isDialogOpen &&
      // Offline: inline payments are online-only (raw api.post). Don't pre-fill
      // a cash row the only-enabled button would silently discard — the vet
      // records cash via the offline-capable Record Payment dialog instead.
      skipWhenOffline() &&
      // No active clinic payment type → every value this row could hold is one
      // the server rejects, so don't pre-fill a row that can only 400.
      !noRecordablePaymentTypes &&
      !hasAutoAddedPaymentRow.current &&
      !willSplit &&
      formData.lineItems && formData.lineItems.length > 0 &&
      invoiceTotal > 0 &&
      inlinePayments.length === 0 &&
      !selectedInvoice?.finalizedAt
    ) {
      hasAutoAddedPaymentRow.current = true;
      const priorPaid = selectedInvoice?.totalPaid ?? 0;
      const remaining = Math.max(0, invoiceTotal - priorPaid);
      setInlinePayments([{
        id: `pay-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        paymentType: defaultInlinePaymentType,
        paymentDate: todayLocalISO(),
        amount: remaining > 0 ? remaining.toFixed(2) : '',
        referenceNote: '',
      }]);
    }
  }, [isDialogOpen, formData.lineItems, formData.amount, formData.taxAmount, inlinePayments.length, selectedInvoice, defaultInlinePaymentType, noRecordablePaymentTypes]);

  // Keep the auto-created payment row amount in sync with the invoice total
  // as the user continues editing line items (adding items, changing prices).
  useEffect(() => {
    if (
      !hasAutoAddedPaymentRow.current ||
      hasManuallyEditedPayment.current ||
      inlinePayments.length !== 1 ||
      selectedInvoice?.finalizedAt
    ) return;
    const invoiceTotal = (formData.amount || 0) + (formData.taxAmount || 0);
    const priorPaid = selectedInvoice?.totalPaid ?? 0;
    const remaining = Math.max(0, invoiceTotal - priorPaid);
    const newAmount = remaining > 0 ? remaining.toFixed(2) : '';
    if (inlinePayments[0].amount !== newAmount) {
      setInlinePayments(prev => [{ ...prev[0], amount: newAmount }]);
    }
  }, [formData.amount, formData.taxAmount, inlinePayments, selectedInvoice]);

  const CARD_PAYMENT_TYPES = ['credit_card', 'debit_card'];

  const recordInlinePayments = async (invoiceId: string, skipCardTypes = false) => {
    const validPayments = inlinePayments.filter(p => {
      if (p.paymentType === 'payment_link') return false;
      const amt = parseFloat(p.amount);
      if (isNaN(amt) || amt <= 0) return false;
      if (skipCardTypes && CARD_PAYMENT_TYPES.includes(p.paymentType)) return false;
      return true;
    });
    for (const payment of validPayments) {
      await api.post('/payments/record', {
        invoiceId,
        amount: parseFloat(payment.amount),
        paymentType: payment.paymentType,
        // The inline payment rows carry a bare `yyyy-MM-dd` (seeded by
        // todayLocalISO, edited through a `type="date"` input) — anchor it the
        // same way the payment dialogs do (DATE-KINDS §2.5 W6).
        paymentDate: ymdToNoonUtcIso(payment.paymentDate) || undefined,
        referenceNote: payment.referenceNote.trim() || undefined,
      });
    }
    return validPayments.length;
  };

  // Check if any valid inline payment uses a card type (needs Stripe to collect card info)
  const hasCardPayment = useMemo(() => {
    return inlinePayments.some(p => {
      const amt = parseFloat(p.amount);
      return !isNaN(amt) && amt > 0 && CARD_PAYMENT_TYPES.includes(p.paymentType);
    });
  }, [inlinePayments]);

  const hasPaymentLink = useMemo(() => {
    return inlinePayments.some(p => p.paymentType === 'payment_link');
  }, [inlinePayments]);

  // Detect if saving will split this invoice between multiple owners: a new
  // invoice for a multi-owner horse (default), or an existing invoice being
  // re-billed back to the owners (billToType 'owners'). Billing a third-party
  // payer produces a single invoice, so no split applies.
  const isNewSplitInvoice = useMemo(() => {
    if (formData.billToType === 'third_party') return false;
    if (selectedInvoice) return formData.billToType === 'owners';
    const horse = horses.find(h => h.id === formData.horseId);
    return horse ? billableOwnersOf(horse.owners).length > 1 : false;
  }, [selectedInvoice, formData.horseId, formData.billToType, horses]);

  // Save invoice WITHOUT processing any inline payments — always available escape hatch
  // so the user can save a draft/pending invoice even when payment lines have been entered.
  const handleSaveAsPendingOnly = async () => {
    // The owners-only offline pre-check that lived here is now redundant and gone:
    // `updateInvoice` refuses EVERY payer switch while offline (I-8), on this door and
    // on the two others (handleSave / handleSaveAndCollectPayment), in one sentence —
    // and the catch below already surfaces an OfflineWriteBlockedError verbatim.
    try {
      if (selectedInvoice) {
        const result = await updateInvoice.mutateAsync({ id: selectedInvoice.id, data: formData });
        if (Array.isArray(result)) {
          enqueueSnackbar(`Invoice split into ${result.length} invoices — collect payments from each individually`, { variant: 'success' });
        } else if (formData.billToType === 'owners') {
          // Backend found a single LINKED owner (Horse.owners JSON can list
          // more) and re-billed in full instead of splitting — say so.
          enqueueSnackbar(`Re-billed in full to ${result?.client?.name || 'the owner on file'} — this horse has one linked owner`, { variant: 'info' });
        }
      } else {
        const result = await createInvoice.mutateAsync(formData);
        if (Array.isArray(result)) {
          enqueueSnackbar(`Invoice split into ${result.length} invoices — collect payments from each individually`, { variant: 'success' });
        }
      }
      handleCloseDialog();
    } catch (error: any) {
      console.error('Failed to save invoice:', error);
      if (error instanceof OfflineWriteBlockedError || error?.name === 'OfflineWriteBlockedError') {
        enqueueSnackbar(error.message, { variant: 'error' });
        return;
      }
      const msg = error?.response?.data?.message;
      enqueueSnackbar(Array.isArray(msg) ? msg.join(', ') : (msg || 'Failed to save invoice'), { variant: 'error' });
    }
  };

  // Save invoice + record non-card payments.
  // If a card payment type was selected, open the EnhancedPaymentDialog to collect card details.
  const handleSaveAsPending = async () => {
    // The owners re-split is online-only — this is the button actually
    // rendered for it (isNewSplitInvoice routes here), so the dedicated
    // message must fire BEFORE the generic card-payments one below.
    if (selectedInvoice && formData.billToType === 'owners' && !skipWhenOffline()) {
      enqueueSnackbar('Splitting an invoice between owners needs a connection — try again when back online.', { variant: 'warning' });
      return;
    }
    // This handler records INLINE payments (raw api.post, no offline path) and
    // opens the Stripe/payment-link dialogs — keep it online-only, but point the
    // vet at the offline-capable path.
    if (!skipWhenOffline()) {
      enqueueSnackbar(
        'Card payments and payment links need a connection — save as pending, then use Record Payment for cash/check.',
        { variant: 'warning' },
      );
      return;
    }
    try {
      let savedInvoice: any;
      let invoiceId: string;
      if (selectedInvoice) {
        const updated = await updateInvoice.mutateAsync({ id: selectedInvoice.id, data: formData });
        // Re-billing to multiple owners returns the new split group as an array
        if (Array.isArray(updated)) {
          enqueueSnackbar(`Invoice split into ${updated.length} invoices — collect payments from each individually`, { variant: 'success' });
          handleCloseDialog();
          return;
        }
        if (formData.billToType === 'owners') {
          // Single linked owner → backend re-billed in full instead of
          // splitting. Stop here either way: the Payments section was hidden
          // for this flow, so any auto-added inline payment row was invisible
          // to the user and must NOT be recorded.
          enqueueSnackbar(`Re-billed in full to ${updated?.client?.name || 'the owner on file'} — this horse has one linked owner`, { variant: 'info' });
          handleCloseDialog();
          return;
        }
        savedInvoice = updated || selectedInvoice;
        invoiceId = selectedInvoice.id;
      } else {
        savedInvoice = await createInvoice.mutateAsync(formData);
        // Backend returns an array for split invoices
        if (Array.isArray(savedInvoice)) {
          enqueueSnackbar(`Invoice split into ${savedInvoice.length} invoices — collect payments from each individually`, { variant: 'success' });
          handleCloseDialog();
          return;
        }
        invoiceId = savedInvoice.id;
      }

      // Capture card total BEFORE closing dialog (which clears inlinePayments)
      const cardTotal = hasCardPayment
        ? inlinePayments
            .filter(p => CARD_PAYMENT_TYPES.includes(p.paymentType))
            .reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0)
        : 0;

      // Record non-card payments immediately; card payments need the Stripe dialog
      const hasValidPayments = inlinePayments.some(p => parseFloat(p.amount) > 0);
      if (hasValidPayments) {
        await recordInlinePayments(invoiceId, hasCardPayment);
        queryClient.invalidateQueries(['invoices']); queryClient.invalidateQueries(['billing-stats']);
      }

      const shouldOpenPaymentLink = hasPaymentLink;
      const linkAmount = shouldOpenPaymentLink
        ? inlinePayments
            .filter(p => p.paymentType === 'payment_link')
            .reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0)
        : 0;

      handleCloseDialog();

      if (shouldOpenPaymentLink) {
        setPaymentLinkInvoice(savedInvoice);
        setPaymentLinkAmount(linkAmount > 0 ? linkAmount : undefined);
        setPaymentLinkDialogOpen(true);
      } else if (hasCardPayment) {
        setPendingInvoiceForPayment(savedInvoice);
        setPendingCardAmount(cardTotal > 0 ? cardTotal : undefined);
        setPaymentDialogCardOnly(true);
        setPaymentDialogOpen(true);
      }
    } catch (error: any) {
      console.error('Failed to save invoice:', error);
      if (error instanceof OfflineWriteBlockedError || error?.name === 'OfflineWriteBlockedError') {
        enqueueSnackbar(error.message, { variant: 'error' });
        return;
      }
      const msg = error?.response?.data?.message;
      enqueueSnackbar(Array.isArray(msg) ? msg.join(', ') : (msg || 'Failed to save invoice'), { variant: 'error' });
    }
  };

  const handleSaveAndCollectPayment = async () => {
    // Offline-capable: saves the invoice (queued offline) then opens the payment
    // dialog, which records cash/check offline and hides card/payment-link.
    try {
      let savedInvoice: Invoice;
      if (selectedInvoice) {
        const updated = await updateInvoice.mutateAsync({ id: selectedInvoice.id, data: formData });
        savedInvoice = updated || selectedInvoice;
      } else {
        const result = await createInvoice.mutateAsync(formData);
        if (Array.isArray(result)) {
          enqueueSnackbar(`Invoice split into ${result.length} invoices for multiple owners — collect payments from each individually`, { variant: 'success' });
          handleCloseDialog();
          return;
        }
        savedInvoice = result;
      }

      handleCloseDialog();
      // A $0 invoice comes back paid (settled by its discount): nothing to collect.
      if (savedInvoice.status === 'paid') return;
      setPendingInvoiceForPayment(savedInvoice);
      setPaymentDialogCardOnly(false);
      setPaymentDialogOpen(true);
    } catch (error: any) {
      if (error instanceof OfflineWriteBlockedError || error?.name === 'OfflineWriteBlockedError') {
        enqueueSnackbar(error.message, { variant: 'error' });
        return;
      }
      const msg = error?.response?.data?.message;
      enqueueSnackbar(Array.isArray(msg) ? msg.join(', ') : (msg || 'Failed to save invoice'), { variant: 'error' });
    }
  };

  // Legacy handleSubmit for backwards compatibility with status-only updates
  const handleSubmit = async () => {
    // This is now only used for status updates on paid/cancelled invoices
    if (selectedInvoice) {
      updateInvoice.mutate({ id: selectedInvoice.id, data: formData }, {
        onSuccess: () => handleCloseDialog(),
      });
    }
  };

  const handlePaymentSuccess = () => {
    trackFeatureUsed('payment_recorded');
    setPaymentDialogOpen(false);
    setPendingInvoiceForPayment(null);
    setPaymentDialogCardOnly(false);
    queryClient.invalidateQueries(['invoices']); queryClient.invalidateQueries(['billing-stats']);
  };

  const handleDelete = (id: string) => {
    if (window.confirm('Are you sure you want to delete this invoice?')) {
      deleteInvoice.mutate(id);
    }
  };

  const findAppointmentPrice = (type: string): { basePrice: number; description: string } | null => {
    const nameLower = type.toLowerCase().trim();
    const clinicItems = catalogItems.filter(i => i.clinicId && i.isActive && i.category === 'appointment');
    const globalItems = catalogItems.filter(i => !i.clinicId && i.isActive && i.category === 'appointment');
    const items = [...clinicItems, ...globalItems];

    const match = items.find(item => item.name.toLowerCase().trim() === nameLower);
    if (match) return { basePrice: match.basePrice, description: match.name };

    const fallback = APPOINTMENT_PRICING[type];
    if (fallback) return { basePrice: fallback.basePrice, description: fallback.description };

    return null;
  };

  const handleAppointmentChange = (appointmentId: string) => {
    if (!appointmentId) {
      // "No appointment - Custom Invoice": back to a FRESH default invoice —
      // exactly what a new custom invoice opens with. Keeping the previous
      // appointment's horse/Bill To/fee row around confused users (and the
      // fee row's billed bookkeeping rides solely on the cleared link, so
      // keeping it would let the appointment be billed twice later).
      setSelectedAppointment(null);
      const days = clinicData?.defaultPaymentDays ?? 30;
      setFormData({
        ...initialFormData,
        taxRate: clinicData?.taxRate || 0,
        dueDate: addDays(new Date(), days),
        serviceDate: new Date(),
      });
      setInlinePayments([]);
      hasAutoAddedPaymentRow.current = false;
      hasManuallyEditedPayment.current = false;
      return;
    }
    const appointment = appointments.find(a => a.id === appointmentId);
    if (appointment) {
      setSelectedAppointment(appointment);
      
      const newLineItems: LineItem[] = [];
      const pricing = findAppointmentPrice(appointment.type);
      
      if (pricing) {
        newLineItems.push({
          description: `${appointment.type} - ${pricing.description}`,
          quantity: 1,
          unitPrice: pricing.basePrice,
          total: pricing.basePrice,
          category: 'appointment',
        });
      }
      
      setFormData(prev => {
        const { taxAmount, amount } = recalcAmountWithTax(newLineItems, prev.taxRate, formInvoiceDiscount(prev));
        const newServiceDate = parseISO(formatInstantInZone(appointment.date, clinicTz, 'yyyy-MM-dd'));
        const days = clinicData?.defaultPaymentDays ?? 30;
        return {
          ...prev,
          appointmentId,
          horseId: appointment.horseId,
          serviceDate: newServiceDate,
          dueDate: addDays(newServiceDate, days),
          lineItems: newLineItems,
          taxAmount,
          amount,
          appointmentIds: [],
          medicationIds: [],
          vaccinationIds: [],
          procedureIds: [],
        };
      });
    }
  };

  const clinicTaxMode = clinicData?.taxMode || 'ALL';

  // BILL-DISC: the form's invoice-level discount as an InvoiceDiscount object.
  const formInvoiceDiscount = (fd: BillFormData): InvoiceDiscount | null =>
    fd.discountType && fd.discountValue
      ? { discountType: fd.discountType, discountValue: fd.discountValue, discountReason: fd.discountReason || undefined }
      : null;

  // Helper: recalculate amount and tax from line items.
  // BILL-DISC calculation order: line discounts (inside item.total) → invoice
  // discount → tax on the discounted base. amount = POST-discount subtotal,
  // matching the backend's stored Invoice.amount.
  const recalcAmountWithTax = (items: LineItem[], taxRate: number, discount: InvoiceDiscount | null = null) => {
    const subtotal = items.reduce((sum, item) => sum + item.total, 0);
    const discountAmount = computeInvoiceDiscountAmount(subtotal, discount);
    const taxAmount = calculateTax(items, taxRate, clinicTaxMode, discountAmount);
    const amount = Math.round((subtotal - discountAmount) * 100) / 100;
    return { subtotal, taxAmount, amount };
  };

  // Import unbilled items for a horse (used when navigating from Horse Profile)
  const importUnbilledItemsMutation = useMutation(
    (horseId: string) => api.get(`/billing/unbilled-items/horse/${horseId}`).then(res => res.data),
    {
      onSuccess: (data) => {
        const { lineItems: unbilledItems, appointmentIds, medicationIds, vaccinationIds, procedureIds } = data;
        
        if (unbilledItems.length === 0) {
          enqueueSnackbar('No unbilled items found for this patient in the last 30 days. You can still create a custom invoice.', { variant: 'info' });
          return;
        }

        setFormData(prev => {
          const allItems = [...(prev.lineItems || []), ...unbilledItems];
          const { taxAmount, amount } = recalcAmountWithTax(allItems, prev.taxRate, formInvoiceDiscount(prev));
          return {
            ...prev,
            horseId: data.horseId,
            lineItems: allItems,
            appointmentIds: [...(prev.appointmentIds ?? []), ...(appointmentIds ?? [])],
            medicationIds: [...(prev.medicationIds || []), ...medicationIds],
            vaccinationIds: [...(prev.vaccinationIds || []), ...vaccinationIds],
            procedureIds: [...(prev.procedureIds || []), ...procedureIds],
            taxAmount,
            amount,
          };
        });
      },
      onError: (error: any) => {
        enqueueSnackbar(error.response?.data?.message || 'Failed to load unbilled items', { variant: 'error' });
      },
    }
  );

  // Handle navigation from appointments page or horse profile with auto-import
  useEffect(() => {
    const state = location.state as { 
      appointmentId?: string; 
      autoImport?: boolean; 
      invoiceId?: string; 
      editMode?: boolean;
      horseId?: string;
      autoImportHorse?: boolean;
    } | null;
    
    // Handle invoice navigation from AI assistant or clinical page.
    // Since the list is server-paginated, the target invoice may not be
    // on the current page — fetch it directly if missing.
    if (state?.invoiceId) {
      const inPage = invoices.find((inv) => inv.id === state.invoiceId);
      const openFound = (invoice: Invoice) => {
        if (state.editMode === false) {
          handleViewInvoice(invoice);
        } else {
          handleOpenDialog(invoice);
        }
      };

      if (inPage) {
        openFound(inPage);
        window.history.replaceState({}, document.title);
        return;
      }

      // Not on current page — fetch directly (replica fallback when offline).
      withOfflineFallback(
        () => api.get(`/billing/${state.invoiceId}`).then((r) => r.data),
        () => readInvoice(state.invoiceId!) as any,
      )()
        .then((data) => {
          if (!data) return; // not found in replica — ignore
          const invoice = normaliseInvoice(data) as Invoice;
          openFound(invoice);
          window.history.replaceState({}, document.title);
        })
        .catch(() => {
          /* invoice not found — ignore */
        });
      return;
    }

    // Handle auto-import from Horse Profile
    if (state?.horseId && state?.autoImportHorse && hasImportedRef.current !== `horse-${state.horseId}`) {
      // Wait for clinic data to load (need tax rate)
      if (isLoadingClinic) return;
      
      hasImportedRef.current = `horse-${state.horseId}`;
      
      const defaultTaxRate = clinicData?.taxRate || 0;
      const days = clinicData?.defaultPaymentDays ?? 30;
      setSelectedInvoice(null);
      setFormData({
        ...initialFormData,
        taxRate: defaultTaxRate,
        horseId: state.horseId!,
        dueDate: addDays(new Date(), days),
      });
      setIsHorseImport(true);
      setIsDialogOpen(true);
      
      // Fetch unbilled items (online-only endpoint) — degrade with guidance
      // offline instead of an error toast on every chart -> Create Invoice.
      if (skipWhenOffline()) {
        setTimeout(() => {
          importUnbilledItemsMutation.mutate(state.horseId!);
        }, 300);
      } else {
        enqueueSnackbar('Unbilled-item import needs a connection — add line items manually.', {
          variant: 'info',
        });
      }
      
      window.history.replaceState({}, document.title);
      return;
    }
    
    // Handle auto-import from Appointments page
    if (state?.appointmentId && state?.autoImport && hasImportedRef.current !== state.appointmentId) {
      // Wait for clinic data to load (need tax rate)
      if (isLoadingClinic) return;
      
      hasImportedRef.current = state.appointmentId;
      
      setSelectedInvoice(null);
      setFormData({
        ...initialFormData,
        taxRate: clinicData?.taxRate || 0,
        dueDate: addDays(new Date(), clinicData?.defaultPaymentDays ?? 30),
      });
      setIsDialogOpen(true);
      
      // Select the appointment which will trigger handleAppointmentChange
      handleAppointmentChange(state.appointmentId);

      // Auto-import clinical data after a short delay to let the appointment
      // load. The import is an online-only endpoint — mirror the horse-import
      // gate above and degrade with guidance instead of an error toast.
      if (skipWhenOffline()) {
        setTimeout(() => {
          importClinicalDataMutation.mutate(state.appointmentId!);
        }, 500);
      } else {
        enqueueSnackbar('Clinical-data import needs a connection — add line items manually.', {
          variant: 'info',
        });
      }

      // Clear the location state
      window.history.replaceState({}, document.title);
    }
  }, [location.state, invoices, isLoadingClinic, clinicData]);

  const handleViewInvoice = async (invoice: Invoice) => {
    // Fetch full invoice detail (includes ownerAddress from Client table)
    try {
      const fullInvoice: any = await withOfflineFallback(
        () => api.get(`/billing/${invoice.id}`).then((r) => r.data),
        () => readInvoice(invoice.id) as any,
      )();
      // Parse lineItems if needed
      let lineItems = fullInvoice.lineItems || [];
      if (typeof lineItems === 'string') {
        try { lineItems = JSON.parse(lineItems); } catch { lineItems = []; }
      }
      setDetailInvoice({ ...fullInvoice, lineItems });
    } catch {
      // Fallback to list data if detail fetch fails
      setDetailInvoice(invoice);
    }
    setInvoiceDetailOpen(true);
  };

  const handleLineItemsChange = (lineItems: LineItem[]) => {
    setFormData(prev => {
      // BILL-DISC: subtotal is post-line-discount; the invoice discount then
      // reduces it (and the taxable base) before tax — mirrors the backend.
      const subtotal = lineItems.reduce((sum, item) => sum + item.total, 0);
      const discount = formInvoiceDiscount(prev);
      const discountAmount = computeInvoiceDiscountAmount(subtotal, discount);
      const discounted = Math.round((subtotal - discountAmount) * 100) / 100;
      const fullTax = calculateTax(lineItems, prev.taxRate, clinicTaxMode, discountAmount);
      const pct = selectedInvoice?.splitPercentage;
      const amount = pct != null ? Math.round(discounted * (pct / 100) * 100) / 100 : discounted;
      const taxAmount = pct != null ? Math.round(fullTax * (pct / 100) * 100) / 100 : fullTax;
      return {
        ...prev,
        lineItems,
        taxAmount,
        amount,
      };
    });
  };

  // BILL-DISC: invoice-level discount changed in the editor footer.
  const handleInvoiceDiscountChange = (discount: InvoiceDiscount | null) => {
    setFormData(prev => {
      const items = prev.lineItems || [];
      const { taxAmount, amount } = recalcAmountWithTax(items, prev.taxRate, discount);
      return {
        ...prev,
        discountType: discount?.discountType ?? null,
        discountValue: discount?.discountValue ?? null,
        discountReason: discount?.discountReason ?? null,
        taxAmount,
        amount,
      };
    });
  };

  if (isLoadingInvoices) {
    return (
      <Box p={3}>
        <Typography>Loading invoices...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ overflowX: 'hidden', p: { xs: 1, sm: 1.5 } }}>
      <Box 
        display="flex" 
        justifyContent="space-between" 
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        flexDirection={{ xs: 'column', sm: 'row' }}
        gap={{ xs: 1, sm: 0 }}
        mb={{ xs: 1, sm: 1.5 }}
      >
        <Box>
          <Typography 
            variant="h4" 
            gutterBottom
            sx={{ 
              fontSize: { xs: '1.25rem', sm: '1.5rem', md: '2.125rem' },
              fontWeight: 700,
              mb: { xs: 0.5, sm: 0.5 },
            }}
          >
            Invoicing
          </Typography>
          <Typography 
            color="textSecondary"
            sx={{ 
              fontSize: { xs: '0.8rem', sm: '0.875rem' },
              display: { xs: 'none', sm: 'block' },
            }}
          >
            Manage invoices, payments, and financial records
          </Typography>
        </Box>
        <Box sx={{ position: 'relative' }}>
          {can.add && (
          <>
          <ButtonGroup
            variant="contained"
            ref={splitBtnRef}
            data-tour="invoice-create-button"
            sx={{
              '& .MuiButtonGroup-grouped': {
                bgcolor: '#3B2E1A',
                '&:hover': { bgcolor: '#2A2012' },
                textTransform: 'none',
                fontWeight: 500,
                fontSize: { xs: '0.8rem', sm: '0.875rem' },
                minHeight: 44,
              },
              '& .MuiButtonGroup-grouped:not(:last-of-type)': {
                borderColor: 'rgba(255,255,255,0.2)',
              },
            }}
          >
            <Button startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>
              New Invoice
            </Button>
            <Button
              size="small"
              onClick={() => setSplitBtnOpen((prev) => !prev)}
              sx={{ px: 0.75, minWidth: '0 !important' }}
              aria-label="invoice type options"
            >
              <ArrowDropDownIcon />
            </Button>
          </ButtonGroup>
          <Popper
            open={splitBtnOpen}
            anchorEl={splitBtnRef.current}
            transition
            disablePortal
            placement="bottom-end"
            style={{ zIndex: 1300 }}
          >
            {({ TransitionProps }) => (
              <Grow {...TransitionProps}>
                <Paper elevation={4} sx={{ mt: 0.5, minWidth: 260 }}>
                  <ClickAwayListener onClickAway={() => setSplitBtnOpen(false)}>
                    <MenuList dense>
                      <MenuItem
                        onClick={() => {
                          setSplitBtnOpen(false);
                          handleOpenDialog();
                        }}
                      >
                        <ListItemText primary="Create a new invoice" />
                      </MenuItem>
                      <MenuItem
                        onClick={() => {
                          setSplitBtnOpen(false);
                          setClientInvoiceWizardOpen(true);
                        }}
                      >
                        {/* "Create a client invoice", not "Create a statement" (Ali,
                            2026-09-21): S5 renamed every statement surface, and this menu
                            entry is the one it should not have. Here the two entries are a
                            choice between documents you can CREATE, and what separates them
                            is who each is FOR — a CLIENT invoice beside the patient invoice
                            above. The Statements TAB, the "Statement #" column, the wizard,
                            the emails and the filenames keep the S5 wording. */}
                        <ListItemText primary="Create a client invoice" />
                      </MenuItem>
                      {/* ⚠ Bulk mark-paid is a PAYMENT, not a status edit: `/billing/bulk-paid`
                          posts `/payments/record-batch` (manage:payments), and
                          `PUT /billing/:id/status` has no web caller at all. It keeps its own
                          gate inside the split button — and gets a standalone button BELOW for
                          the person who holds payments but not charges, for whom this whole
                          menu is hidden. */}
                      {can.bulkMarkPaid && (
                      <MenuItem
                        onClick={() => {
                          setSplitBtnOpen(false);
                          navigate('/billing/bulk-paid');
                        }}
                      >
                        <ListItemText primary="Record bulk payment" />
                      </MenuItem>
                      )}
                    </MenuList>
                  </ClickAwayListener>
                </Paper>
              </Grow>
            )}
          </Popper>
          </>
          )}
          {/*
            THE FRONT DESK THAT MAY TAKE MONEY BUT NOT WRITE INVOICES.
            `manage:payments` and `manage:charges` are separate slugs precisely so a clinic can
            grant one without the other, and bulk mark-paid lived INSIDE the `manage:charges`
            split button — so the one screen it is for was the one place a payments-only person
            could not reach it. (The selection bar's "Record Payment(s)" is a different flow: it
            needs invoices already selected in the list, where this is the batch entry screen.)
            Standalone here, same route, same slug, only when the split button is not rendered.
          */}
          {!can.add && can.bulkMarkPaid && (
            <Button
              variant="contained"
              startIcon={<PaymentIcon />}
              onClick={() => navigate('/billing/bulk-paid')}
              sx={{
                bgcolor: '#3B2E1A',
                '&:hover': { bgcolor: '#2A2012' },
                textTransform: 'none',
                fontWeight: 500,
                fontSize: { xs: '0.8rem', sm: '0.875rem' },
                minHeight: 44,
              }}
            >
              Record bulk payment
            </Button>
          )}
        </Box>
      </Box>

      <Grid container spacing={{ xs: 1, sm: 1.5, md: 2 }} mb={{ xs: 1.5, sm: 2 }}>
        <Grid item xs={6} sm={6} md={3}>
          <StatCard
            title={isMobile ? "Revenue" : "Total Revenue"}
            value={formatDollars(stats.totalRevenue)}
            subtitle={`${stats.totalCount} invoices`}
            icon={<MoneyIcon />}
          />
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <StatCard
            title="Outstanding"
            value={formatDollars(stats.outstandingAmount)}
            subtitle={`${stats.pendingCount} outstanding`}
            caption={
              // Every figure on this header renders through `money.ts`
              // (`.claude/rules/frontend.md`): a raw `toLocaleString()` prints 2712.5 as
              // "$2,712.5" and 237.497 as "$237.497" — a float's own precision on a page
              // clinics read for money. `formatDollars` fixes `en-US` and two decimals.
              // No leading "+": it is ALREADY subtracted from the figure above,
              // so a plus sign read as "$30 still to come on top of
              // Outstanding" — the caption contradicting its own tooltip
              // (code-backend M10). The wording now says both halves.
              stats.processingAmount > 0
                ? `${formatDollars(stats.processingAmount)} in flight (ACH), already deducted`
                : undefined
            }
            captionTitle="ACH payments that have not settled yet. They are already deducted from Outstanding and Past due."
            testId="tile-outstanding"
            icon={<ScheduleIcon />}
          />
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <StatCard
            title="Past due"
            // Offline the replica cannot derive past due — the split needs the
            // CLINIC's calendar day — so the composer above returns 0 and the
            // whole open balance lands under Outstanding. Rendering that 0 here
            // turned a missing figure into a positive claim: an offline clinic
            // with $10k past due read "PAST DUE · $0.00 · 0 past due". Suppress
            // it instead, the way the Dashboard tile and `offline/readers.ts`
            // already describe (the money is not hidden — it is in Outstanding).
            value={isOffline ? '—' : formatDollars(stats.pastDueAmount)}
            valueTitle={
              isOffline
                ? "Past due needs a connection (the clinic's calendar day). The whole open balance is shown under Outstanding."
                : undefined
            }
            subtitle={isOffline ? 'Needs a connection' : `${stats.overdueCount} past due`}
            testId="tile-past-due"
            icon={<WarningIcon />}
          />
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <StatCard
            title={isMobile ? "Average" : "Average Invoice"}
            value={formatDollars(stats.averageInvoice)}
            subtitle="Per paid invoice"
            icon={<TrendingUpIcon />}
          />
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <StatCard
            title={isMobile ? "Paid (30d)" : "Paid · Last 30 Days"}
            value={formatDollars(stats.paidAmount30Days)}
            subtitle={`${stats.paidCount30Days} paid`}
            icon={<PaymentIcon />}
          />
        </Grid>
      </Grid>

      <Paper sx={{ mb: 2, borderRadius: 3, boxShadow: 'none', border: '1px solid', borderColor: 'divider' }}>
        <Box p={1.5}>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>Filters</Typography>
          
          {/* Active filter chip with back button */}
          {(clientFilter || horseFilter) && (
            <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Button
                size="small"
                startIcon={<ArrowBackIcon />}
                onClick={() => {
                  if (horseFilter) {
                    navigate(`/clinical-records/${horseFilter}`);
                  } else if (clientFilter) {
                    navigate('/clients', { state: { selectedClientId: clientFilter } });
                  }
                }}
                sx={{ textTransform: 'none' }}
              >
                Back to {horseFilter ? 'Patient' : 'Client'}
              </Button>
              <Chip
                label={`Showing invoices for: ${filterDisplayName}`}
                onDelete={handleClearFilter}
                color="primary"
                variant="outlined"
              />
            </Box>
          )}
          
          <Box display="flex" gap={2}>
            <TextField
              placeholder="Search invoices..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              size="small"
            />
            <TextField
              select
              label="Date Range"
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              size="small"
            >
              {INVOICE_DATE_RANGES.map((range) => (
                <MenuItem key={range.value} value={range.value}>
                  {range.label}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        </Box>
        <Tabs
          value={statusFilter}
          onChange={(_, value) => setStatusFilter(value)}
          sx={{
            px: 1.5,
            borderTop: '1px solid',
            borderColor: 'divider',
            '& .MuiTab-root': { textTransform: 'none', fontWeight: 500, fontSize: '0.875rem', color: 'text.secondary', '&.Mui-selected': { color: '#C9772B', fontWeight: 600 } },
            '& .MuiTabs-indicator': { backgroundColor: '#C9772B' },
          }}
        >
          <Tab label="All Invoices" value="all" />
          <Tab label="Pending" value="pending" />
          <Tab label="Paid" value="paid" />
          <Tab label="Overdue" value="overdue" />
          <Tab label="Statements" value="statements" />
        </Tabs>
      </Paper>

      {selectedInvoiceIds.size > 0 && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            px: 2,
            py: 1,
            mb: 1,
            bgcolor: 'rgba(139,69,19,0.05)',
            border: '1px solid',
            borderColor: 'rgba(139,69,19,0.2)',
            borderRadius: 2,
          }}
        >
          <Typography variant="body2" fontWeight={600} color="text.secondary">
            {selectedInvoiceIds.size} selected
          </Typography>
          {/* Both buttons in this bar TAKE money — payments.controller.ts `recordBatch` :49-50
              and `record` :40-41 — so they ask manage:payments, never the invoice slug. */}
          {can.recordPayment && (
          <Button
            variant="contained"
            size="small"
            onClick={() => {
              setBulkPayRefs({});
              // '' when the clinic has no active type left, which keeps Confirm
              // disabled instead of re-submitting a stale fallback choice.
              setBulkPayType(recordablePaymentTypes[0]?.value ?? '');
              setBulkPayModalOpen(true);
            }}
            sx={{ bgcolor: '#8B4513', '&:hover': { bgcolor: '#7a3c10' }, textTransform: 'none', fontWeight: 600 }}
          >
            Record Payment{selectedInvoiceIds.size > 1 ? 's' : ''}
          </Button>
          )}
          {can.recordPayment && (
          <Tooltip
            title={selectedInvoiceIds.size > 1 ? 'Select one invoice to collect payment' : ''}
          >
            <span>
              <Button
                variant="outlined"
                size="small"
                disabled={selectedInvoiceIds.size > 1}
                onClick={() => {
                  const inv = filteredInvoices.find((i) => selectedInvoiceIds.has(i.id));
                  if (inv) {
                    setPendingInvoiceForPayment(inv);
                    setPaymentDialogOpen(true);
                  }
                }}
                sx={{ textTransform: 'none', borderColor: 'divider', color: 'text.secondary' }}
              >
                Collect Payment
              </Button>
            </span>
          </Tooltip>
          )}
          <Box sx={{ flexGrow: 1 }} />
          <Button
            variant="text"
            size="small"
            onClick={() => setSelectedInvoiceIds(new Set())}
            sx={{ textTransform: 'none', color: 'text.secondary' }}
          >
            Clear selection
          </Button>
        </Box>
      )}

      {/* Discovery hint — only when there are open invoices and none selected */}
      {selectedInvoiceIds.size === 0 &&
        filteredInvoices.some((i) => OPEN_STATUSES.includes(i.status)) && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', px: 2, py: 0.75, mb: 0.5 }}
          >
            Tip: select invoices with the checkboxes to record a bulk payment.
          </Typography>
        )}

      {statusFilter === 'statements' ? (
        <StatementsTab clientId={clientFilter ?? undefined} />
      ) : (
      <TableContainer data-tour="invoice-list" data-testid="invoice-list" component={Paper} sx={{ overflowX: 'auto', boxShadow: 'none', border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
        <Table sx={{ minWidth: 650 }}>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  size="small"
                  checked={(() => { const open = filteredInvoices.filter((i) => OPEN_STATUSES.includes(i.status)); return open.length > 0 && open.every((i) => selectedInvoiceIds.has(i.id)); })()}
                  indeterminate={(() => { const open = filteredInvoices.filter((i) => OPEN_STATUSES.includes(i.status)); return open.some((i) => selectedInvoiceIds.has(i.id)) && !open.every((i) => selectedInvoiceIds.has(i.id)); })()}
                  onChange={toggleAllInvoices}
                  sx={{ '&.Mui-checked, &.MuiCheckbox-indeterminate': { color: '#8B4513' } }}
                />
              </TableCell>
              <TableCell>Invoice #</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Client & Patient</TableCell>
              <TableCell>Service</TableCell>
              <TableCell align="right">Amount</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredInvoices.map((invoice) => (
              <TableRow key={invoice.id} data-testid="invoice-row" hover selected={selectedInvoiceIds.has(invoice.id)}>
                <TableCell padding="checkbox">
                  <Checkbox
                    size="small"
                    checked={selectedInvoiceIds.has(invoice.id)}
                    disabled={!OPEN_STATUSES.includes(invoice.status)}
                    onChange={() => toggleInvoiceSelection(invoice.id)}
                    onClick={(e) => e.stopPropagation()}
                    sx={{ '&.Mui-checked': { color: '#8B4513' } }}
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight="medium" data-testid="invoice-number">
                    {invoice.invoiceNumber}
                  </Typography>
                </TableCell>
                <TableCell>{format(parseISO(invoice.issuedAt), 'MMM d, yyyy')}</TableCell>
                <TableCell>
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <Box>
                      <Typography variant="body2" fontWeight="medium">
                        {invoice.client?.name || invoice.thirdPartyPayer?.name || invoice.horse.ownerName || (() => {
                          const mainOwner = Array.isArray(invoice.horse.owners) && invoice.horse.owners.length > 0
                            ? invoice.horse.owners[0]
                            : null;
                          return mainOwner?.name || 'Unknown';
                        })()}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        Patient: {invoice.horse.name}
                      </Typography>
                    </Box>
                    {invoice.splitGroupId && (
                      <Chip
                        icon={<CallSplitIcon sx={{ fontSize: 14 }} />}
                        label="Split"
                        size="small"
                        variant="outlined"
                        color="info"
                        sx={{ ml: 0.5, height: 22, '& .MuiChip-label': { px: 0.5, fontSize: '0.7rem' } }}
                      />
                    )}
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {invoice.appointments?.[0]?.type || 'Custom Service'}
                  </Typography>
                  {invoice.description && (
                    <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
                      {invoice.description.length > 50 
                        ? `${invoice.description.substring(0, 50)}...` 
                        : invoice.description}
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2" fontWeight="medium" data-testid="invoice-amount">
                    ${((invoice.amount || 0) + (invoice.taxAmount || 0)).toFixed(2)}
                  </Typography>
                  {/* A5c — the Amount above is the GROSS document total and this
                      row's Balance Due is settled-only, so nothing here has been
                      deducted: the caption says "in flight", never "deducted"
                      (the Outstanding TILE, which IS net, says the opposite for
                      the same reason). Absent offline — the replica row carries
                      no processingAmount (INVR145). */}
                  {(invoice.processingAmount ?? 0) > 0 && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: 'block' }}
                      data-testid="invoice-processing"
                      title={`An ACH payment of ${formatDollars(invoice.processingAmount!)} has not settled yet. It is not deducted from this invoice's amount or its balance due.`}
                    >
                      {formatDollars(invoice.processingAmount!)} processing (ACH)
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Box display="flex" gap={0.5} flexWrap="wrap">
                    <StatusChip status={invoice.status} />
                    {invoice.finalizedAt && (
                      <Chip label="Finalized" size="small" color="info" />
                    )}
                    {invoice.paymentLinkUrl && invoice.status !== 'paid' && invoice.status !== 'cancelled' && (
                      <Chip
                        icon={<LinkIcon sx={{ fontSize: 14 }} />}
                        label="Link Sent"
                        size="small"
                        variant="outlined"
                        color="primary"
                        sx={{ height: 22, '& .MuiChip-label': { px: 0.5, fontSize: '0.7rem' } }}
                      />
                    )}
                  </Box>
                </TableCell>
                <TableCell align="right">
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() => handleViewInvoice(invoice)}
                    title="View Invoice"
                    data-testid="invoice-view"
                  >
                    <VisibilityIcon fontSize="small" />
                  </IconButton>
                  {can.recordPayment && invoice.status !== 'paid' && invoice.status !== 'cancelled' && (
                    <IconButton
                      size="small"
                      color="success"
                      onClick={() => {
                        setPendingInvoiceForPayment(invoice);
                        setPaymentDialogOpen(true);
                      }}
                      // Offline-capable: the payment dialog records cash/check
                      // offline (and hides card/payment-link itself).
                      title="Record Payment"
                    >
                      <PaymentIcon fontSize="small" />
                    </IconButton>
                  )}
                  {can.edit && (
                    <IconButton
                      size="small"
                      onClick={() => handleOpenDialog(invoice)}
                      title={invoice.finalizedAt ? "Cannot edit finalized invoice" : "Edit Invoice"}
                      disabled={!!invoice.finalizedAt}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  )}
                  {can.remove && (
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDelete(invoice.id)}
                      // Invoice deletion stays online-only (never queued).
                      title={isOffline ? 'Deleting an invoice needs a connection' : 'Delete Invoice'}
                      disabled={!!invoice.finalizedAt || isOffline}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={totalInvoices}
          page={invoicePage}
          onPageChange={(_, newPage) => setInvoicePage(newPage)}
          rowsPerPage={invoiceRowsPerPage}
          rowsPerPageOptions={[invoiceRowsPerPage]}
        />
      </TableContainer>
      )}

      <Dialog open={isDialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle sx={{ pb: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
            {clinicData?.logoUrl ? (
              <Box
                component="img"
                src={clinicData.logoUrl}
                alt={clinicData?.name}
                sx={{ maxHeight: 56, maxWidth: 140, objectFit: 'contain' }}
              />
            ) : (
              <ReceiptIcon color="primary" sx={{ fontSize: 36, mt: 0.5 }} />
            )}
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" fontWeight="bold">
                {selectedInvoice ? 'Edit Invoice' : 'New Invoice'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {clinicData?.name || 'Veterinary Clinic'}
                {clinicData?.phone && ` · ${clinicData.phone}`}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <Divider sx={{ mt: 1.5 }} />
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <Grid container spacing={1.5}>
              {/* Only show appointment selector for manual invoice creation (not horse import) */}
              {!isHorseImport && !selectedInvoice && (
                <Grid item xs={12}>
                  <Stack direction="row" spacing={2} alignItems="flex-start">
                    <FormControl fullWidth data-testid="appointment-picker">
                      <InputLabel>Related Appointment (Optional)</InputLabel>
                      <Select
                        value={formData.appointmentId || ''}
                        onChange={(e) => handleAppointmentChange(e.target.value)}
                        label="Related Appointment (Optional)"
                      >
                        <MenuItem value="">No appointment - Custom Invoice</MenuItem>
                        {availableAppointments.map((appointment) => {
                          // A3: already billed → show it, disabled, naming the
                          // invoice it is on. invoice.invoiceNumber comes
                          // straight off GET /appointments; invoiceId without
                          // the relation (an offline replica row) still
                          // disables, just without a number.
                          const billedNumber = appointment.invoice?.invoiceNumber;
                          const isBilled = !!appointment.invoiceId || !!appointment.invoice;
                          return (
                            <MenuItem key={appointment.id} value={appointment.id} disabled={isBilled}>
                              {formatInstantInZone(appointment.date, clinicTz, 'MMM d, yyyy')} - {appointment.horse.name} ({appointment.type})
                              {isBilled
                                ? billedNumber
                                  ? ` — already invoiced (${billedNumber})`
                                  : ' — already invoiced'
                                : ''}
                            </MenuItem>
                          );
                        })}
                      </Select>
                      <FormHelperText>
                        Select an appointment to auto-calculate service pricing or import clinical data
                      </FormHelperText>
                    </FormControl>
                    {formData.appointmentId && (
                      <Button
                        variant="outlined"
                        onClick={() => importClinicalDataMutation.mutate(formData.appointmentId!)}
                        // Online-only endpoint pair (clinical summary + catalog).
                        disabled={importClinicalDataMutation.isLoading || isOffline}
                        title={isOffline ? 'Clinical-data import needs a connection' : undefined}
                        sx={{ mt: 1, minWidth: 200 }}
                      >
                        {importClinicalDataMutation.isLoading ? 'Importing...' : 'Import Clinical Data'}
                      </Button>
                    )}
                  </Stack>
                </Grid>
              )}

              <Grid item xs={12} md={6}>
                <Autocomplete
                  fullWidth
                  options={horses}
                  value={horses.find((h) => h.id === formData.horseId) ?? null}
                  // Like the dropdown this replaced: a picked patient can be changed, never emptied.
                  disableClearable={!!formData.horseId}
                  onChange={(_, newHorse) => {
                    const newHorseId = newHorse?.id ?? '';
                    // Clinical records belong to a patient: switching horses
                    // drops the imported medication/vaccination/procedure IDs
                    // and any appointment fee row, so the previous horse's
                    // records can't be linked and marked billed under the new
                    // horse's invoice. Payer switches reset too —
                    // 'owners'/'owner:<name>' would cross-wire to the old
                    // horse (the backend also rejects that combination).
                    const keptItems = (formData.lineItems || []).filter(it => it.category !== 'appointment');
                    const { taxAmount, amount } = recalcAmountWithTax(keptItems, formData.taxRate, formInvoiceDiscount(formData));
                    setFormData({
                      ...formData,
                      horseId: newHorseId,
                      lineItems: keptItems,
                      taxAmount,
                      amount,
                      medicationIds: [],
                      vaccinationIds: [],
                      procedureIds: [],
                      ...(selectedInvoice && formData.billToType !== 'third_party'
                        ? { billToType: 'current' }
                        : {}),
                    });
                    const multiOwner = newHorse && Array.isArray(newHorse.owners) && newHorse.owners.length > 1;
                    if (multiOwner) {
                      setInlinePayments([]);
                      hasAutoAddedPaymentRow.current = true;
                    } else {
                      hasAutoAddedPaymentRow.current = false;
                    }
                  }}
                  getOptionLabel={invoicePatientLabel}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  // Two horses can share a name, so the rows are keyed by id.
                  renderOption={(props, horse) => (
                    <li {...props} key={horse.id}>
                      {invoicePatientLabel(horse)}
                    </li>
                  )}
                  disabled={!!formData.appointmentId || isHorseImport}
                  renderInput={(params) => (
                    <TextField {...params} label="Horse/Patient" placeholder="Search by name or patient ID" required />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  label="Service Date"
                  type="date"
                  value={format(formData.serviceDate, 'yyyy-MM-dd')}
                  onChange={(e) => {
                    if (e.target.value) {
                      const d = parseISO(e.target.value);
                      if (isValid(d)) {
                        const days = clinicData?.defaultPaymentDays ?? 30;
                        setFormData(prev => ({ ...prev, serviceDate: d, dueDate: addDays(d, days) }));
                      }
                    }
                  }}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  helperText="Date services were provided"
                />
              </Grid>

              {/* Bill To — payer summary and switching */}
              {formData.horseId && (() => {
                const selectedHorse = horses.find(h => h.id === formData.horseId);
                // Billable set only: a 0% owner is never a Bill To target
                const owners = billableOwnersOf(selectedHorse?.owners);
                const isMultiOwner = owners.length > 1;
                const isThirdParty = formData.billToType === 'third_party';

                const contactBox = (title: string, people: Array<{ name: string; email?: string; phone?: string; address?: string }>) => (
                  <Box sx={{ bgcolor: '#f8f9fa', borderRadius: 1, px: 2, py: 1.5, border: '1px solid #e0e0e0' }}>
                    <Typography variant="caption" color="text.secondary" fontWeight="bold" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      {title}
                    </Typography>
                    {people.map((person, idx) => (
                      <Box key={idx} sx={{ mt: 0.5 }}>
                        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'center' }}>
                          <Typography variant="body2" fontWeight="medium">{person.name}</Typography>
                          {person.email && <Typography variant="body2" color="text.secondary">{person.email}</Typography>}
                          {person.phone && <Typography variant="body2" color="text.secondary">{person.phone}</Typography>}
                        </Box>
                        {person.address && (
                          <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-line' }}>
                            {person.address}
                          </Typography>
                        )}
                      </Box>
                    ))}
                  </Box>
                );
                const ownersBox = owners.length > 0
                  ? contactBox(isMultiOwner ? `Bill To (${owners.length} Owners)` : 'Bill To', owners)
                  : null;

                const payerFields = (
                  <>
                    <Grid item xs={12} md={4}>
                      <TextField
                        label="Payer Name"
                        value={formData.billToName || ''}
                        onChange={(e) => setFormData({ ...formData, billToName: e.target.value })}
                        fullWidth
                        required
                        size="small"
                        placeholder="e.g., Insurance company, trainer, barn"
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        label="Payer Email (optional)"
                        type="email"
                        value={formData.billToEmail || ''}
                        onChange={(e) => setFormData({ ...formData, billToEmail: e.target.value })}
                        fullWidth
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        label="Payer Phone (optional)"
                        value={formData.billToPhone || ''}
                        onChange={(e) => setFormData({ ...formData, billToPhone: e.target.value })}
                        fullWidth
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        label="Billing Address (optional)"
                        value={formData.billToAddress || ''}
                        onChange={(e) => setFormData({ ...formData, billToAddress: e.target.value })}
                        fullWidth
                        size="small"
                        multiline
                        minRows={2}
                        placeholder="Street, city, state, zip — shown on the invoice"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Box sx={{ bgcolor: '#fff8e1', borderRadius: 1, px: 2, py: 1.5, border: '1px solid #ffe082' }}>
                        <Typography variant="body2" color="text.secondary">
                          The payer doesn't need to be an owner of {selectedHorse?.name || 'the horse'}. Their details are recorded on this invoice only — they won't be added to your client list.
                        </Typography>
                      </Box>
                    </Grid>
                  </>
                );

                const handleBillToChange = (value: string) => {
                  // The Payments section is hidden while 'owners' is selected
                  // (payments are collected per split invoice) — drop any
                  // auto-added inline row so a payment the user can't see is
                  // never silently recorded on save.
                  if (value === 'owners') {
                    setInlinePayments([]);
                  }
                  setFormData({
                    ...formData,
                    billToType: value,
                    billToName: '',
                    billToEmail: '',
                    billToPhone: '',
                    billToAddress: '',
                  });
                };

                // Finalized (or paid/cancelled) invoices — payer is locked, summary only.
                //
                // I-8/M-5: a RECORDED PAYMENT locks it too, and that case is neither
                // finalized nor paid — `payments.service` stamps `finalizedAt` only when
                // the balance clears, so a partially_paid invoice left the selector open,
                // let the biller type an insurer's name/email/phone/address, and only then
                // answered 400 "Cannot re-bill an invoice that already has payments."
                // (`assertPayerSwitchAllowed`). Pre-empting it is a locked control with a
                // reason instead of a late refusal on a billing document.
                //
                // ⚠ What `totalPaid` can and cannot see: both list mappers select
                // `payments: { where: { status: 'succeeded' } }` (billing.service.ts), so
                // it covers every SUCCEEDED row — an applied account CREDIT among them
                // (credits.service writes a succeeded row) — and never counts
                // voided/refunded/failed history, which the server allows a switch over.
                // It does NOT see a `processing` ACH row, which the server DOES count: that
                // one still surfaces as the server's 400 on save. No false locks, and the
                // common case learns early.
                const settledPaymentRecorded = (selectedInvoice?.totalPaid ?? 0) > 0;
                const payerLocked = selectedInvoice && (
                  selectedInvoice.finalizedAt || formData.status === 'paid'
                  || formData.status === 'cancelled' || settledPaymentRecorded
                );
                if (payerLocked) {
                  const lockReason = selectedInvoice!.finalizedAt
                    ? 'This invoice is finalized — the payer can no longer be changed.'
                    : formData.status === 'cancelled'
                      ? 'This invoice is voided — the payer can no longer be changed.'
                      : formData.status === 'paid'
                        ? 'This invoice is paid — the payer can no longer be changed.'
                        : 'A payment has been recorded on this invoice — refund or void it before re-billing this invoice to someone else.';
                  const lockedBox = selectedInvoice!.client
                    ? contactBox('Bill To', [selectedInvoice!.client])
                    : selectedInvoice!.thirdPartyPayer
                      ? contactBox('Bill To (Third-Party Payer)', [selectedInvoice!.thirdPartyPayer])
                      : ownersBox;
                  return lockedBox ? (
                    <Grid item xs={12}>
                      {lockedBox}
                      <FormHelperText data-testid="payer-lock-reason">{lockReason}</FormHelperText>
                    </Grid>
                  ) : null;
                }

                // Editing an existing invoice — allow switching the payer.
                // Multi-owner horses re-bill as the owner SPLIT (the
                // create-time default, one invoice per owner by ownership
                // percentage) — never a single co-owner for the full amount;
                // single-owner horses re-bill to that owner directly.
                if (selectedInvoice) {
                  const currentName = selectedInvoice.client?.name || selectedInvoice.thirdPartyPayer?.name;
                  const selectedOwner = formData.billToType?.startsWith('owner:')
                    ? owners.find(o => `owner:${o.name}` === formData.billToType)
                    : null;
                  const isOwnersResplit = formData.billToType === 'owners';
                  // Already-split invoices can't be split again.
                  const canResplit = isMultiOwner && !selectedInvoice.splitGroupId;
                  return (
                    <Grid item xs={12}>
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <FormControl fullWidth size="small" data-testid="bill-to-select">
                            <InputLabel>Bill To</InputLabel>
                            <Select
                              value={formData.billToType || 'current'}
                              onChange={(e) => handleBillToChange(e.target.value)}
                              label="Bill To"
                            >
                              <MenuItem value="current">
                                {currentName ? `Current payer — ${currentName}` : 'Current payer — none on file'}
                              </MenuItem>
                              {/* I-8: EVERY switch is online-only, so all three options carry the
                                  same offline gate the owners re-split already had — the sync
                                  UPDATE arm refuses billToOwnerName and billToThirdParty too.
                                  Keeping the CURRENT payer is not a switch and stays enabled. */}
                              {canResplit && (
                                <MenuItem value="owners" disabled={isOffline}>
                                  Owners — split between {owners.length} owners{isOffline ? ' (needs a connection)' : ''}
                                </MenuItem>
                              )}
                              {!isMultiOwner && owners.map((owner) => (
                                <MenuItem key={`owner:${owner.name}`} value={`owner:${owner.name}`} disabled={isOffline}>
                                  Owner — {owner.name}{isOffline ? ' (needs a connection)' : ''}
                                </MenuItem>
                              ))}
                              <MenuItem value="third_party" disabled={isOffline}>
                                Third-Party Payer{isOffline ? ' (needs a connection)' : ''}
                              </MenuItem>
                            </Select>
                            <FormHelperText>
                              {isThirdParty
                                ? 'On save, this invoice is re-billed to the payer entered below'
                                : isOwnersResplit
                                  ? 'On save, this invoice is split — each owner receives their own invoice based on ownership percentage'
                                  : selectedOwner
                                    ? 'On save, this invoice is re-billed to this owner'
                                    : 'The current payer is kept'}
                            </FormHelperText>
                          </FormControl>
                        </Grid>
                        {isThirdParty ? payerFields : (
                          <Grid item xs={12}>
                            {isOwnersResplit
                              ? ownersBox
                              : selectedOwner
                                ? contactBox('Bill To (Owner)', [selectedOwner])
                                : selectedInvoice.client
                                  ? contactBox('Bill To', [selectedInvoice.client])
                                  : selectedInvoice.thirdPartyPayer
                                    ? contactBox('Bill To (Third-Party Payer)', [selectedInvoice.thirdPartyPayer])
                                    : ownersBox}
                          </Grid>
                        )}
                      </Grid>
                    </Grid>
                  );
                }

                // New invoice — let the user choose who gets billed
                return (
                  <Grid item xs={12}>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Bill To</InputLabel>
                          <Select
                            value={formData.billToType || 'owners'}
                            onChange={(e) => handleBillToChange(e.target.value)}
                            label="Bill To"
                          >
                            <MenuItem value="owners">
                              {isMultiOwner
                                ? `Owners — split between ${owners.length} owners`
                                : owners.length === 1
                                  ? `Owner — ${owners[0].name}`
                                  : 'Owner(s) on file'}
                            </MenuItem>
                            <MenuItem value="third_party">Third-Party Payer</MenuItem>
                          </Select>
                          <FormHelperText>
                            {isThirdParty
                              ? 'The full invoice is billed to the payer entered below'
                              : isMultiOwner
                                ? 'Each owner receives their own invoice based on ownership percentage'
                                : 'Invoice is billed to the owner on file'}
                          </FormHelperText>
                        </FormControl>
                      </Grid>
                      {isThirdParty ? payerFields : (
                        <Grid item xs={12}>
                          {ownersBox}
                        </Grid>
                      )}
                    </Grid>
                  </Grid>
                );
              })()}

              <Grid item xs={12}>
                <InvoiceLineItemsEditor
                  lineItems={formData.lineItems || []}
                  onChange={handleLineItemsChange}
                  catalogItems={clinicCatalogOptions}
                  taxRate={formData.taxRate}
                  taxMode={clinicTaxMode}
                  splitPercentage={selectedInvoice?.splitPercentage}
                  splitOwnerTotal={selectedInvoice?.splitPercentage != null
                    ? (Number(formData.amount || 0) + Number(formData.taxAmount || 0))
                    : undefined}
                  // BILL-DISC: discounts are locked once a co-owned invoice has
                  // been split — the backend rejects those edits (shares were
                  // carved from the discounted group total at split time).
                  invoiceDiscount={formInvoiceDiscount(formData)}
                  onInvoiceDiscountChange={selectedInvoice?.splitGroupId ? undefined : handleInvoiceDiscountChange}
                  allowLineDiscounts={!selectedInvoice?.splitGroupId}
                />
              </Grid>

              <Grid item xs={12} md={8}>
                <TextField
                  label="Notes (shown on the invoice)"
                  value={formData.clientNote ?? ''}
                  onChange={(e) => setFormData({ ...formData, clientNote: e.target.value })}
                  fullWidth
                  size="small"
                  multiline
                  maxRows={6}
                  placeholder="Visible to the client on the invoice/receipt"
                  helperText={
                    !selectedInvoice && invoiceSettings?.defaultClientNote
                      ? "Your clinic's default note. Change or delete it for this invoice."
                      : undefined
                  }
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  label="Internal note (accountant only)"
                  value={formData.internalNote || ''}
                  onChange={(e) => setFormData({ ...formData, internalNote: e.target.value })}
                  fullWidth
                  size="small"
                  placeholder="Not shown on printed/emailed invoice"
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  label="Due Date"
                  type="date"
                  value={format(formData.dueDate, 'yyyy-MM-dd')}
                  onChange={(e) => {
                    if (e.target.value) {
                      const d = parseISO(e.target.value);
                      if (isValid(d)) setFormData(prev => ({ ...prev, dueDate: d }));
                    }
                  }}
                  fullWidth
                  required
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              {/* Status dropdown - only show for existing paid/cancelled invoices that need status changes */}
              {selectedInvoice && (formData.status === 'paid' || formData.status === 'cancelled') && (
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={formData.status || 'pending'}
                      onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                      label="Status"
                      // Voids are final (PIN-approved); reactivating would
                      // strand the void audit trail — backend rejects it too.
                      disabled={selectedInvoice?.status === 'cancelled'}
                    >
                      <MenuItem value="pending">Pending Payment</MenuItem>
                      <MenuItem value="paid">Paid in Full</MenuItem>
                      <MenuItem value="overdue">Overdue</MenuItem>
                      {/* Cancelling = VOID (PIN-approved, via the invoice's Void
                          action); only render as the current value so an
                          already-cancelled invoice still displays correctly. */}
                      {selectedInvoice?.status === 'cancelled' && (
                        <MenuItem value="cancelled">Cancelled</MenuItem>
                      )}
                    </Select>
                    <FormHelperText>
                      {selectedInvoice?.status === 'cancelled'
                        ? 'Voided invoices can’t be reactivated — recreate the invoice if needed'
                        : formData.status === 'paid'
                          ? '⚠️ Invoice is finalized (cannot be edited)'
                          : 'Change invoice status — to void, use the Void action on the invoice (manager approval)'}
                    </FormHelperText>
                  </FormControl>
                </Grid>
              )}
            </Grid>
          </Box>

          {/* Split invoice info — payments collected per split invoice */}
          {isNewSplitInvoice && formData.lineItems && formData.lineItems.length > 0 && (
            <Box sx={{ mt: 1, borderTop: '2px solid #e0e0e0', pt: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, p: 2, bgcolor: 'info.lighter', border: '1px solid', borderColor: 'info.light', borderRadius: 1, backgroundColor: '#e3f2fd' }}>
                <CallSplitIcon sx={{ color: 'info.main', mt: 0.25 }} />
                <Box>
                  <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 0.5 }}>
                    This invoice will be split between {horses.find(h => h.id === formData.horseId)?.owners?.length || 0} owners
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Each owner will receive their own invoice based on their ownership percentage. You can collect payments from each split invoice individually after saving.
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}

          {/* Inline Payments Section — hidden for new split invoices */}
          {!isNewSplitInvoice && formData.lineItems && formData.lineItems.length > 0 && !selectedInvoice?.finalizedAt && (
            <Box sx={{ mt: 1, borderTop: '2px solid #e0e0e0', pt: 2 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
                <Typography variant="subtitle2" fontWeight="bold" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary' }}>
                  Payments
                </Typography>
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  // Nothing valid to put in the row's Type select — adding one
                  // would only produce a 400 on save (see the Alert below).
                  disabled={noRecordablePaymentTypes}
                  onClick={() => {
                    const invoiceTotal = (formData.amount || 0) + (formData.taxAmount || 0);
                    const currentInlineTotal = inlinePayments.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
                    // For existing invoices, start from the actual remaining balance (accounting for prior recorded payments)
                    const priorPaid = selectedInvoice?.totalPaid ?? 0;
                    const actualRemaining = Math.max(0, invoiceTotal - priorPaid - currentInlineTotal);
                    setInlinePayments(prev => [...prev, {
                      id: `pay-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
                      paymentType: defaultInlinePaymentType,
                      paymentDate: todayLocalISO(),
                      amount: actualRemaining > 0 ? actualRemaining.toFixed(2) : '',
                      referenceNote: '',
                    }]);
                  }}
                >
                  Add Payment
                </Button>
              </Box>

              {inlinePayments.map((payment, index) => (
                <Box key={payment.id} sx={{ display: 'flex', gap: 1, mb: 1.5, alignItems: 'flex-start', flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
                  <FormControl size="small" sx={{ minWidth: 150 }} data-testid="inline-payment-type">
                    <InputLabel>Type</InputLabel>
                    <Select
                      value={payment.paymentType}
                      onChange={(e) => setInlinePayments(prev => prev.map(p => p.id === payment.id ? { ...p, paymentType: e.target.value } : p))}
                      label="Type"
                    >
                      {[
                        // The clinic's own types, same source as the client
                        // invoice screen. Send-a-link is a Stripe flow rather
                        // than a configured type, so it is appended here and
                        // only when Connect can actually charge.
                        ...recordablePaymentTypes,
                        ...(stripeConnectEnabled
                          ? [{ value: 'payment_link', label: 'Payment Link' }]
                          : []),
                      ].map(t => (
                        <MenuItem key={t.value} value={t.value}>
                          {t.value === 'payment_link' ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <LinkIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                              {t.label}
                            </Box>
                          ) : t.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <TextField
                    size="small"
                    type="date"
                    label="Date"
                    value={payment.paymentDate}
                    onChange={(e) => setInlinePayments(prev => prev.map(p => p.id === payment.id ? { ...p, paymentDate: e.target.value } : p))}
                    InputLabelProps={{ shrink: true }}
                    sx={{ width: 155 }}
                  />
                  <TextField
                    size="small"
                    label="Amount"
                    value={payment.amount}
                    onChange={(e) => {
                      hasManuallyEditedPayment.current = true;
                      setInlinePayments(prev => prev.map(p => p.id === payment.id ? { ...p, amount: e.target.value } : p));
                    }}
                    InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                    sx={{ width: 125 }}
                  />
                  <TextField
                    size="small"
                    label="Reference"
                    value={payment.referenceNote}
                    onChange={(e) => setInlinePayments(prev => prev.map(p => p.id === payment.id ? { ...p, referenceNote: e.target.value } : p))}
                    placeholder={INLINE_REF_PLACEHOLDERS[payment.paymentType] || 'Optional'}
                    sx={{ flex: 1, minWidth: 120 }}
                  />
                  <IconButton
                    size="small"
                    onClick={() => setInlinePayments(prev => prev.filter(p => p.id !== payment.id))}
                    color="error"
                    sx={{ mt: 0.5 }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}

              {noRecordablePaymentTypes ? (
                <Alert severity="warning" sx={{ my: 1 }}>
                  {NO_PAYMENT_TYPES_HELP}
                </Alert>
              ) : (
                inlinePayments.length === 0 && (
                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 1.5, fontStyle: 'italic' }}>
                    No payment recorded yet. Use "Add Payment" to record a payment, or save as pending.
                  </Typography>
                )
              )}

              {/* Payment Summary */}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, pt: 1.5, borderTop: '1px solid #e0e0e0' }}>
                <Box sx={{ minWidth: 250 }}>
                  <Box display="flex" justifyContent="space-between" mb={0.5}>
                    <Typography variant="body2" color="text.secondary">Invoice Total</Typography>
                    <Typography variant="body2" fontWeight="medium">
                      ${((formData.amount || 0) + (formData.taxAmount || 0)).toFixed(2)}
                    </Typography>
                  </Box>
                  {/* Show previously recorded payments for existing invoices */}
                  {selectedInvoice && (selectedInvoice.totalPaid ?? 0) > 0 && (
                    <Box display="flex" justifyContent="space-between" mb={0.5}>
                      <Typography variant="body2" color="text.secondary">Previously Paid</Typography>
                      <Typography variant="body2" fontWeight="medium" color="success.main">
                        ${(selectedInvoice.totalPaid ?? 0).toFixed(2)}
                      </Typography>
                    </Box>
                  )}
                  {inlinePayments.length > 0 && (
                    <Box display="flex" justifyContent="space-between" mb={0.5}>
                      <Typography variant="body2" color="text.secondary">Paying Now</Typography>
                      <Typography variant="body2" fontWeight="medium" color="success.main">
                        ${inlinePayments.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0).toFixed(2)}
                      </Typography>
                    </Box>
                  )}
                  <Divider sx={{ my: 0.5 }} />
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="subtitle2" fontWeight="bold">Amount Due</Typography>
                    <Typography
                      variant="subtitle2"
                      fontWeight="bold"
                      color={(() => {
                        const priorPaid = selectedInvoice?.totalPaid ?? 0;
                        const payingNow = inlinePayments.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
                        const due = (formData.amount || 0) + (formData.taxAmount || 0) - priorPaid - payingNow;
                        return due <= 0 ? 'success.main' : 'text.primary';
                      })()}
                    >
                      {(() => {
                        const priorPaid = selectedInvoice?.totalPaid ?? 0;
                        const payingNow = inlinePayments.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
                        const due = (formData.amount || 0) + (formData.taxAmount || 0) - priorPaid - payingNow;
                        return due <= 0 ? '$0.00 ✓' : `$${Math.max(0, due).toFixed(2)}`;
                      })()}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1.5 }}>
          <Button onClick={handleCloseDialog} size="large">
            Cancel
          </Button>
          <Box sx={{ flex: 1 }} />
          
          {isNewSplitInvoice ? (
            <Button
              onClick={handleSaveAsPending}
              variant="contained"
              color="primary"
              size="large"
              disabled={!formData.horseId || !(formData.lineItems && formData.lineItems.length > 0) || createInvoice.isLoading || updateInvoice.isLoading}
              startIcon={<CallSplitIcon />}
            >
              Save & Split Invoice
            </Button>
          ) : selectedInvoice && (formData.status === 'paid' || formData.status === 'cancelled') ? (
            <Button
              onClick={handleSubmit}
              variant="contained"
              color="primary"
              size="large"
              disabled={!formData.horseId || updateInvoice.isLoading}
              startIcon={<ReceiptIcon />}
            >
              Update Invoice
            </Button>
          ) : (() => {
            const invoiceTotal = (formData.amount || 0) + (formData.taxAmount || 0);
            const priorPaid = selectedInvoice?.totalPaid ?? 0;
            const validPaymentsTotal = inlinePayments.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
            const fullyPaid = validPaymentsTotal > 0 && (priorPaid + validPaymentsTotal) >= invoiceTotal;
            const hasPayment = validPaymentsTotal > 0;
            const hasLineItems = formData.lineItems && formData.lineItems.length > 0;
            const missingPayer = formData.billToType === 'third_party' && !formData.billToName?.trim();
            const isDisabled = !formData.horseId || !hasLineItems || missingPayer || createInvoice.isLoading || updateInvoice.isLoading;
            const isEditing = !!selectedInvoice;
            // INLINE-payment + card/payment-link CTAs are disabled offline (raw
            // api.post / Stripe). Cash & check work offline via the payment
            // dialog, so "Save & Collect Payment" stays ENABLED. A disabled
            // button must explain itself — tooltip, not a dead snackbar guard.
            const paymentsBlocked = !skipWhenOffline();
            const offlineBlockedTip =
              'Card payments and payment links need a connection — save as pending, then use Record Payment for cash/check.';
            const withOfflineTip = (button: React.ReactElement) =>
              paymentsBlocked ? (
                <Tooltip title={offlineBlockedTip}>
                  <span>{button}</span>
                </Tooltip>
              ) : (
                button
              );

            // Always-visible "Save as Pending" escape hatch — saves the invoice without
            // processing any inline payments, regardless of what's entered below.
            const pendingButton = (
              <Button
                onClick={handleSaveAsPendingOnly}
                variant="outlined"
                color="primary"
                size="large"
                disabled={isDisabled}
                startIcon={<ReceiptIcon />}
              >
                Save as Pending
              </Button>
            );

            // Discounted to $0: the server marks it paid and finalizes it on save, so there is
            // nothing to collect (billing/invoice-lines.ts isSettledByDiscount).
            const settledByDiscount =
              hasLineItems &&
              Math.abs(invoiceTotal) < 0.005 &&
              (!!formData.discountType || (formData.lineItems ?? []).some((li: any) => !!li.discountType));
            if (settledByDiscount) {
              return (
                <Button
                  onClick={handleSaveAsPendingOnly}
                  variant="contained"
                  color="success"
                  size="large"
                  disabled={isDisabled}
                  startIcon={<ReceiptIcon />}
                >
                  {isEditing ? 'Update & Finalize (nothing due)' : 'Save & Finalize (nothing due)'}
                </Button>
              );
            }

            if (hasPaymentLink) {
              return (
                <>
                  {pendingButton}
                  {withOfflineTip(
                    <Button
                      onClick={handleSaveAsPending}
                      variant="contained"
                      color="primary"
                      size="large"
                      disabled={isDisabled || paymentsBlocked}
                      startIcon={<LinkIcon />}
                    >
                      {isEditing ? 'Update & Send Payment Link' : 'Save & Send Payment Link'}
                    </Button>,
                  )}
                </>
              );
            }

            if (fullyPaid) {
              // State 1: Inline payments cover the full balance
              const cardTotal = inlinePayments
                .filter(p => CARD_PAYMENT_TYPES.includes(p.paymentType))
                .reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
              return (
                <>
                  {pendingButton}
                  {withOfflineTip(
                    <Button
                      onClick={handleSaveAsPending}
                      variant="contained"
                      color="success"
                      size="large"
                      disabled={isDisabled || paymentsBlocked}
                      startIcon={<MoneyIcon />}
                    >
                      {hasCardPayment
                        ? (isEditing ? `Update & Charge Card — $${cardTotal.toFixed(2)}` : `Save & Charge Card — $${cardTotal.toFixed(2)}`)
                        : (isEditing ? 'Update — Paid in Full' : 'Save — Paid in Full')}
                    </Button>,
                  )}
                </>
              );
            }

            if (hasPayment) {
              // State 2: Partial payment recorded
              const cardTotal = inlinePayments
                .filter(p => CARD_PAYMENT_TYPES.includes(p.paymentType))
                .reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
              return (
                <>
                  {pendingButton}
                  {withOfflineTip(
                    <Button
                      onClick={handleSaveAsPending}
                      variant="contained"
                      color="primary"
                      size="large"
                      disabled={isDisabled || paymentsBlocked}
                      startIcon={hasCardPayment ? <PaymentIcon /> : <ReceiptIcon />}
                    >
                      {hasCardPayment
                        ? (isEditing ? `Update & Charge Card — $${cardTotal.toFixed(2)}` : `Save & Charge Card — $${cardTotal.toFixed(2)}`)
                        : (isEditing ? 'Update Invoice' : 'Save Invoice')}
                    </Button>,
                  )}
                </>
              );
            }

            // State 3: No payment entered — pending button + collect-payment CTA
            return (
              <>
                {pendingButton}
                <Button
                  onClick={handleSaveAndCollectPayment}
                  variant="contained"
                  color="primary"
                  size="large"
                  // Offline-capable: saves (queues) the invoice, then the payment
                  // dialog records cash/check offline.
                  disabled={isDisabled}
                  startIcon={<MoneyIcon />}
                >
                  {isEditing ? 'Update & Collect Payment' : 'Save & Collect Payment'}
                </Button>
              </>
            );
          })()}
        </DialogActions>
      </Dialog>
      {detailInvoice && (
        <InvoiceDetail
          open={invoiceDetailOpen}
          onClose={() => {
            setInvoiceDetailOpen(false);
            setDetailInvoice(null);
          }}
          onRecordPayment={() => {
            // Offline-capable: EnhancedPaymentDialog records cash/check offline
            // and hides card/payment-link itself.
            setInvoiceDetailOpen(false);
            setPendingInvoiceForPayment(detailInvoice);
            setPaymentDialogCardOnly(false);
            setPaymentDialogOpen(true);
          }}
          invoice={detailInvoice}
          onSplit={() => {
            // The payer/split banner re-billed this invoice: the group is new,
            // so the stale detail row must go rather than be re-rendered.
            queryClient.invalidateQueries(['invoices']);
            queryClient.invalidateQueries(['billing-stats']);
            setInvoiceDetailOpen(false);
            setDetailInvoice(null);
          }}
        />
      )}

      {/* Payment Collection Dialog */}
      {pendingInvoiceForPayment && (
        <EnhancedPaymentDialog
          open={paymentDialogOpen}
          onClose={() => {
            setPaymentDialogOpen(false);
            setPendingInvoiceForPayment(null);
            setPaymentDialogCardOnly(false);
            setPendingCardAmount(undefined);
          }}
          onSuccess={handlePaymentSuccess}
          invoice={{
            id: pendingInvoiceForPayment.id,
            invoiceNumber: pendingInvoiceForPayment.invoiceNumber,
            amount: (pendingInvoiceForPayment.amount || 0) + (pendingInvoiceForPayment.taxAmount || 0),
            taxAmount: pendingInvoiceForPayment.taxAmount,
            dueDate: pendingInvoiceForPayment.dueDate,
            horseName: pendingInvoiceForPayment.horse?.name,
            clientId: pendingInvoiceForPayment.clientId || pendingInvoiceForPayment.client?.id,
            clientName: pendingInvoiceForPayment.clientName || pendingInvoiceForPayment.client?.name,
            horse: pendingInvoiceForPayment.horse,
            client: pendingInvoiceForPayment.client,
            // B1: this dialog forwards the invoice to SendPaymentLinkDialog, so the
            // payer has to survive BOTH hand-built objects or the pay link addresses
            // the horse's owner on a third-party-billed invoice.
            thirdPartyPayer: pendingInvoiceForPayment.thirdPartyPayer,
            paymentLinkUrl: pendingInvoiceForPayment.paymentLinkUrl,
          }}
          stripeConnectEnabled={stripeConnectEnabled}
          cardOnly={paymentDialogCardOnly}
          requestedAmount={pendingCardAmount}
          onPaymentLinkSent={() => {
            queryClient.invalidateQueries(['invoices']);
            queryClient.invalidateQueries(['billing-stats']);
          }}
        />
      )}

      {/* Payment Link Dialog (triggered from inline payment type) */}
      {paymentLinkInvoice && (
        <SendPaymentLinkDialog
          open={paymentLinkDialogOpen}
          onClose={() => {
            setPaymentLinkDialogOpen(false);
            setPaymentLinkInvoice(null);
            setPaymentLinkAmount(undefined);
          }}
          onSuccess={() => {
            queryClient.invalidateQueries(['invoices']); queryClient.invalidateQueries(['billing-stats']);
            trackFeatureUsed('payment_link_sent');
          }}
          invoice={{
            id: paymentLinkInvoice.id,
            invoiceNumber: paymentLinkInvoice.invoiceNumber,
            amount: paymentLinkInvoice.amount,
            taxAmount: paymentLinkInvoice.taxAmount,
            status: paymentLinkInvoice.status,
            dueDate: paymentLinkInvoice.dueDate,
            horse: paymentLinkInvoice.horse,
            client: paymentLinkInvoice.client,
            // B1: without this the dialog cannot see the payer and addresses the
            // horse's owner on a third-party-billed invoice.
            thirdPartyPayer: paymentLinkInvoice.thirdPartyPayer,
            paymentLinkUrl: paymentLinkInvoice.paymentLinkUrl,
          }}
          balanceDue={paymentLinkAmount}
        />
      )}
      <ClientInvoiceWizard
        open={clientInvoiceWizardOpen}
        onClose={() => setClientInvoiceWizardOpen(false)}
      />

      {/* ── Bulk Record Payment(s) modal ─────────────────────────────────── */}
      <Dialog
        open={bulkPayModalOpen}
        onClose={() => !bulkPaySubmitting && setBulkPayModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Record Payment{selectedInvoiceIds.size > 1 ? 's' : ''}
          <Typography variant="body2" color="text.secondary" mt={0.25}>
            {selectedInvoiceIds.size} invoice{selectedInvoiceIds.size > 1 ? 's' : ''} selected
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          {/* Loaded and empty — the select below has nothing valid to offer and
              Confirm stays disabled (it already requires a bulkPayType). */}
          {noRecordablePaymentTypes && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {NO_PAYMENT_TYPES_HELP}
            </Alert>
          )}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} mb={2.5}>
            <TextField
              select
              label="Payment method"
              value={bulkPayType}
              onChange={(e) => setBulkPayType(e.target.value)}
              size="small"
              sx={{ minWidth: 180 }}
              required
              disabled={noRecordablePaymentTypes}
            >
              {recordablePaymentTypes.map((t) => (
                <MenuItem key={t.value} value={t.value}>
                  {t.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              type="date"
              label="Date received"
              value={bulkPayDate}
              onChange={(e) => setBulkPayDate(e.target.value)}
              size="small"
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 160 }}
            />
          </Stack>

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: -1.5, mb: 2 }}>
            Records a payment only — no card is charged.
          </Typography>

          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  {['Invoice #', 'Client', 'Patient', 'Amount due', 'Reference number'].map((h, i) => (
                    <TableCell
                      key={h}
                      align={i === 3 ? 'right' : 'left'}
                      sx={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.secondary' }}
                    >
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredInvoices
                  .filter((inv) => selectedInvoiceIds.has(inv.id))
                  .map((inv) => (
                    <TableRow key={inv.id}>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem' }}>{inv.invoiceNumber}</TableCell>
                      <TableCell sx={{ fontSize: '0.8rem' }}>
                        {inv.client?.name ||
                          inv.thirdPartyPayer?.name ||
                          (Array.isArray(inv.horse?.owners) && inv.horse.owners[0]?.name) ||
                          '—'}
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>{inv.horse?.name ?? '—'}</TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.8rem', fontWeight: 600, whiteSpace: 'nowrap' }}>
                        ${(inv.balanceDue ?? Math.max(0, (inv.amount ?? 0) + (inv.taxAmount ?? 0) - (inv.totalPaid ?? 0))).toFixed(2)}
                      </TableCell>
                      <TableCell sx={{ minWidth: 180 }}>
                        <TextField
                          size="small"
                          fullWidth
                          placeholder="Check #, transfer ref, etc."
                          value={bulkPayRefs[inv.id] ?? ''}
                          onChange={(e) =>
                            setBulkPayRefs((prev) => ({ ...prev, [inv.id]: e.target.value }))
                          }
                          sx={{ '& input': { fontSize: '0.8rem' } }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 1.5 }}>
          <Button
            onClick={() => setBulkPayModalOpen(false)}
            disabled={bulkPaySubmitting}
            sx={{ textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleBulkConfirm}
            disabled={bulkPaySubmitting || !bulkPayType}
            startIcon={bulkPaySubmitting ? <CircularProgress size={16} color="inherit" /> : undefined}
            sx={{ bgcolor: '#8B4513', '&:hover': { bgcolor: '#7a3c10' }, textTransform: 'none', fontWeight: 600 }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Billing;